import moment from 'moment';
import { tz } from 'moment-timezone';
import { throwError, timer, lastValueFrom, ReplaySubject } from 'rxjs';
import { mergeMap, switchMap, retryWhen } from 'rxjs/operators';
import { fromFetch } from 'rxjs/fetch';

class AngularZoneUtils {
    static runOutsideAngularZone(func) {
        const Zone = window.Zone;
        if (Zone && Zone.current.name === 'angular') {
            AngularZoneUtils.angularZone = Zone.current;
            console.debug('Running function ' + func.name + ' outside of Angular Zone');
            return this.angularZone.parent.run(func);
        }
        return func();
    }
    static runInsideAngularZone(func) {
        if (AngularZoneUtils.angularZone) {
            console.debug('Running function ' + func.name + ' inside of Angular Zone');
            return AngularZoneUtils.angularZone.run(func);
        }
        return func();
    }
}

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

var _SuppressedError = typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

class AESCrypto {
    constructor() {
        this.algorithm = 'AES-GCM';
        this.keyLength = 256;
        this.ivLength = 12;
    }
    generateKey() {
        return __awaiter(this, void 0, void 0, function* () {
            return crypto.subtle.generateKey({
                name: this.algorithm,
                length: this.keyLength
            }, true, ['encrypt', 'decrypt']);
        });
    }
    importKey(keyData) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!keyData.kty || !keyData.k) {
                throw new Error('Invalid JWK: missing required properties');
            }
            // Decode the base64url encoded key data
            const keyBuffer = Uint8Array.from(atob(keyData.k.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0));
            // Check if the key length matches the expected length for AES-GCM (256 bits / 32 bytes)
            if (keyBuffer.length !== 32) {
                throw new Error('Invalid JWK: key data length is incorrect');
            }
            return crypto.subtle.importKey('jwk', keyData, { name: this.algorithm }, true, ['encrypt', 'decrypt']);
        });
    }
    // Encrypt a string
    encrypt(plainText, key) {
        return __awaiter(this, void 0, void 0, function* () {
            const encoder = new TextEncoder();
            const iv = crypto.getRandomValues(new Uint8Array(this.ivLength)); // Random IV
            const encodedText = encoder.encode(plainText);
            const cipherBuffer = yield crypto.subtle.encrypt({
                name: this.algorithm,
                iv
            }, key, encodedText);
            return {
                cipherText: this.arrayBufferToBase64(cipherBuffer),
                iv: this.arrayBufferToBase64(iv.buffer)
            };
        });
    }
    // Decrypt an encrypted string
    decrypt(cipherText, iv, key) {
        return __awaiter(this, void 0, void 0, function* () {
            const decoder = new TextDecoder();
            const cipherBuffer = this.base64ToArrayBuffer(cipherText);
            const ivBuffer = this.base64ToArrayBuffer(iv);
            const decryptedBuffer = yield crypto.subtle.decrypt({
                name: this.algorithm,
                iv: new Uint8Array(ivBuffer)
            }, key, cipherBuffer);
            return decoder.decode(decryptedBuffer);
        });
    }
    deriveKey(key) {
        return __awaiter(this, void 0, void 0, function* () {
            const encoder = new TextEncoder();
            const keyInBytes = encoder.encode(key);
            const hashBuffer = yield crypto.subtle.digest('SHA-256', keyInBytes);
            const cryptoKey = yield crypto.subtle.importKey('raw', hashBuffer, { name: this.algorithm }, true, // Ensure the key is extractable
            ['encrypt', 'decrypt']);
            return yield crypto.subtle.exportKey('jwk', cryptoKey);
        });
    }
    // Utility: Convert ArrayBuffer to Base64 string
    arrayBufferToBase64(buffer) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        return btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(buffer))));
    }
    // Utility: Convert Base64 string to ArrayBuffer
    base64ToArrayBuffer(base64) {
        const binaryString = atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
    }
}

class ArrayUtils {
    /**
     * move item to the biginnig in the same array. if item not exist - then add it to first place
     *
     * @param arr - array
     * @param element - element to move
     * @param origIndex - index of the element in arr
     */
    static moveItemToFirstIndexInArray(arr, element, origIndex) {
        if (origIndex && origIndex > -1) {
            arr.splice(origIndex, 1);
        }
        arr.unshift(element);
    }
    static getNegativeNumbersFromArray(array) {
        return array.filter(num => num < 0);
    }
    static getPositiveNumbersFromArray(array) {
        return array.filter(num => num > 0);
    }
    static includes(array, value) {
        const filtered = array.filter(item => JSON.stringify(item) === JSON.stringify(value));
        return filtered.length > 0;
    }
}

/* eslint no-bitwise: off */
class ColorUtils {
    static getRGB(b) {
        let a;
        if (b && b.constructor === Array && b.length === 3) {
            return b;
        }
        a = /rgb\(\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*\)/.exec(b);
        if (a) {
            return [parseInt(a[1], 10), parseInt(a[2], 10), parseInt(a[3], 10)];
        }
        a = /rgb\(\s*([0-9]+(?:\.[0-9]+)?)\%\s*,\s*([0-9]+(?:\.[0-9]+)?)\%\s*,\s*([0-9]+(?:\.[0-9]+)?)\%\s*\)/.exec(b);
        if (a) {
            return [parseFloat(a[1]) * 2.55, parseFloat(a[2]) * 2.55, parseFloat(a[3]) * 2.55];
        }
        a = /#([a-fA-F0-9]{2})([a-fA-F0-9]{2})([a-fA-F0-9]{2})/.exec(b);
        if (a) {
            return [parseInt(a[1], 16), parseInt(a[2], 16), parseInt(a[3], 16)];
        }
        a = /#([a-fA-F0-9])([a-fA-F0-9])([a-fA-F0-9])/.exec(b);
        if (a) {
            return [parseInt(a[1] + a[1], 16), parseInt(a[2] + a[2], 16), parseInt(a[3] + a[3], 16)];
        }
        return null;
        // return (typeof (colors) != "undefined") ? colors[$.trim(b).toLowerCase()]:null
    }
    static getLuminance(color) {
        const rgb = ColorUtils.getRGB(color);
        if (!rgb) {
            return null;
        }
        return 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
    }
    static isDarkColor(color) {
        const luminance = ColorUtils.getLuminance(color);
        if (luminance) {
            return luminance < 180;
        }
        return false;
    }
    static rgbToHsl(red, green, blue) {
        const r = red / 255;
        const g = green / 255;
        const b = blue / 255;
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        let h;
        let s;
        const l = (max + min) / 2;
        if (max === min) {
            h = s = 0; // achromatic
        }
        else {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            switch (max) {
                case r:
                    h = (g - b) / d + (g < b ? 6 : 0);
                    break;
                case g:
                    h = (b - r) / d + 2;
                    break;
                case b:
                    h = (r - g) / d + 4;
                    break;
            }
            if (h) {
                h /= 6;
            }
        }
        return [h, s, l];
    }
    static hexToHsl(color, percentage) {
        // Grab the hex representation of red(chars 1 - 2) and convert to decimal(base 10).
        const r = parseInt(color.substr(1, 2), 16);
        const g = parseInt(color.substr(3, 2), 16);
        const b = parseInt(color.substr(5, 2), 16);
        const hsl = ColorUtils.rgbToHsl(r, g, b);
        if (!hsl) {
            return '';
        }
        const hue = hsl[0] * 360;
        const sat = hsl[1] * 100;
        const li = hsl[2] * 100 + percentage;
        return 'hsl(' + hue + ', ' + sat + '%, ' + li + '%)';
    }
    /**
     * Returns a new hexadecimal color that is darkened or lightened by the percentage supplied.
     *
     * @param color  a hexadecimal color including the ampersand.
     * @param percent value between 0 and 1. A negative value will lighten, a positive will darken,
     */
    static shadeColor(color, percent) {
        const f = parseInt(color.slice(1), 16);
        const t = percent < 0 ? 0 : 255;
        const p = percent < 0 ? percent * -1 : percent;
        // tslint:disable:no-bitwise
        const R = f >> 16;
        const G = (f >> 8) & 0x00ff;
        const B = f & 0x0000ff;
        return ('#' +
            (0x1000000 + (Math.round((t - R) * p) + R) * 0x10000 + (Math.round((t - G) * p) + G) * 0x100 + (Math.round((t - B) * p) + B))
                .toString(16)
                .slice(1));
    }
    static calculateCorrespondingColor(color, percentOfChange) {
        const isDarkColorFlag = ColorUtils.isDarkColor(color);
        return isDarkColorFlag ? ColorUtils.shadeColor(color, percentOfChange) : ColorUtils.shadeColor(color, percentOfChange * -1);
    }
}

class CookieUtils {
    static getWindowObject() {
        return window;
    }
    static getBaseHref() {
        const baseElement = document.querySelector('base');
        let href;
        if (baseElement) {
            href = baseElement.getAttribute('href');
        }
        return href ? href.replace(/^(https?:)?\/\/[^/]*/, '') : '';
    }
    static buildCookieString(name, value, options) {
        const parsedValue = typeof value === 'object' ? JSON.stringify(value) : value;
        let cookieString = encodeURIComponent(name) + '=' + encodeURI(parsedValue);
        let cookiePath;
        options = options || {};
        if (options.path) {
            cookiePath = options.path;
        }
        else {
            cookiePath = CookieUtils.getBaseHref();
        }
        cookieString += options.domain ? ';domain=' + options.domain : '';
        cookieString += cookiePath ? ';path=' + cookiePath : '';
        cookieString += options.expires ? ';expires=' + options.expires.toUTCString() : '';
        cookieString += options.maxAge ? ';max-age=' + options.maxAge : '';
        cookieString += options.secure ? ';Secure' : '';
        cookieString += options.samesite && (options.secure || options.samesite !== 'None') ? ';SameSite=' + options.samesite : '';
        return cookieString;
    }
    static setCookie(name, value, options) {
        document.cookie = CookieUtils.buildCookieString(name, value, options);
    }
    static getCookie(name) {
        const value = '; ' + document.cookie;
        const parts = value.split('; ' + name + '=');
        if (parts && parts.length > 1) {
            const cookieValues = parts.pop();
            if (cookieValues) {
                const cookieValue = cookieValues.split(';').shift();
                if (cookieValue) {
                    return decodeURIComponent(cookieValue);
                }
            }
        }
        return undefined;
    }
    static getCookieAsObject(name) {
        let value = this.getCookie(name);
        let parsedValue;
        if (value) {
            try {
                value = value.replace(/(?:\\[rn])+/g, '');
                parsedValue = JSON.parse(value);
                // Our access token cookie is weird as its has escaped the double quotes.
                // So the first time you attempt to JSON.parse() it will remove the escaped quotes but
                // it will return a string and not an Object.  So we need call JSON.parse() a second time
                // to get an Object.
                if ((typeof parsedValue) === 'string') {
                    parsedValue = JSON.parse(parsedValue);
                }
                return parsedValue;
            }
            catch (err) {
                console.error('Could not parse to JSON', err);
            }
        }
        return null;
    }
    static removeCookie(name, options) {
        const date = new Date();
        options = options || {};
        // Expire in -1 days
        date.setTime(date.getTime() + (-1 * 24 * 60 * 60 * 1000));
        options = Object.assign(Object.assign({}, options), { expires: date });
        document.cookie = CookieUtils.buildCookieString(name, '', options);
    }
    static getAccessToken() {
        const cookieObj = CookieUtils.getCookieAsObject(CookieUtils.KEYS.AUTH_COOKIE);
        if (cookieObj) {
            return cookieObj.token;
        }
        else {
            return null;
        }
    }
    static removeAccessToken() {
        CookieUtils.removeCookie(CookieUtils.KEYS.AUTH_COOKIE, { path: '/' });
    }
    static getAccessTokenExpirationTime() {
        const cookieObj = CookieUtils.getCookieAsObject(CookieUtils.KEYS.AUTH_COOKIE);
        if (cookieObj) {
            return cookieObj.tokenExpirationTimeSec;
        }
        else {
            return null;
        }
    }
    static getRefreshToken() {
        const cookieObj = CookieUtils.getCookieAsObject(CookieUtils.KEYS.AUTH_COOKIE);
        if (cookieObj) {
            return cookieObj.refreshToken;
        }
        else {
            return null;
        }
    }
    static getRefreshTokenExpirationTime() {
        const cookieObj = CookieUtils.getCookieAsObject(CookieUtils.KEYS.AUTH_COOKIE);
        if (cookieObj) {
            return cookieObj.refreshTokenExpirationTimeSec;
        }
        else {
            return null;
        }
    }
    static getDomain() {
        const windowObject = CookieUtils.getWindowObject();
        let domain = windowObject.location.hostname.substring(windowObject.location.hostname.indexOf('.'));
        if (domain.toLowerCase().indexOf('.my.') === 0) {
            domain = domain.substring(3);
        }
        return domain;
    }
    static setUserLastActivityTime(userLastActivityTime) {
        const windowObject = CookieUtils.getWindowObject();
        CookieUtils.setCookie(CookieUtils.KEYS.USER_LAST_ACTIVITY_TIME, userLastActivityTime, {
            domain: CookieUtils.getDomain(),
            path: '/',
            samesite: 'None',
            secure: windowObject.location.protocol === 'https:'
        });
    }
    static getUserLastActivityTime() {
        const lastActivityTime = moment(CookieUtils.getCookie(CookieUtils.KEYS.USER_LAST_ACTIVITY_TIME));
        if (lastActivityTime.isValid()) {
            return lastActivityTime;
        }
        else {
            //set old date if valid
            const someDate = new Date();
            someDate.setTime(someDate.getTime() - 3 * 28 * 24 * 60 * 60 * 1000);
            return moment(someDate);
        }
    }
    static getHostDomain(currentEnv) {
        const windowObject = CookieUtils.getWindowObject();
        const host = windowObject.location.hostname;
        let domain;
        if (host.indexOf('localhost') > -1) {
            domain = '.dev.localhost';
        }
        else {
            const hostParts = host.split('.').reverse();
            if (host.indexOf('.perf.') > -1) {
                domain = '.perf.' + hostParts[1] + '.' + hostParts[0];
            }
            else {
                domain = '.' + (currentEnv !== undefined && currentEnv !== '' ?
                    (currentEnv + '.') : '') + hostParts[1] + '.' + hostParts[0];
            }
        }
        return domain;
    }
    static getBrandingHostCookie() {
        const cookieObj = CookieUtils.getCookieAsObject(CookieUtils.KEYS.BRANDING_HOST);
        if (cookieObj) {
            return cookieObj;
        }
        else {
            return null;
        }
    }
    static getBrandingHost(area, currentEnv) {
        const hostDomain = CookieUtils.getHostDomain(currentEnv);
        const currentBrandingHostCookieValue = this.getBrandingHostCookie();
        if (currentBrandingHostCookieValue) {
            return currentBrandingHostCookieValue[area + hostDomain];
        }
        else {
            return null;
        }
    }
    static setBrandingHostCookie(areaIdentifier, currentEnv) {
        const windowObject = CookieUtils.getWindowObject();
        try {
            const host = windowObject.location.hostname;
            const hostParts = host.split('.');
            let subdomain = hostParts[0];
            if (hostParts.length > 1 && hostParts[1].toLowerCase() === 'my') {
                subdomain = subdomain + '.' + hostParts[1];
            }
            const hostDomain = CookieUtils.getHostDomain(currentEnv);
            let brandingHosts = CookieUtils.getBrandingHostCookie();
            if (!brandingHosts) {
                brandingHosts = {};
            }
            if (!areaIdentifier) {
                areaIdentifier = 'na1';
            }
            brandingHosts[areaIdentifier + hostDomain] = subdomain;
            CookieUtils.setCookie(CookieUtils.KEYS.BRANDING_HOST, brandingHosts, {
                domain: CookieUtils.getHostDomain(currentEnv),
                path: '/',
                samesite: 'None',
                secure: windowObject.location.protocol === 'https:'
            });
        }
        catch (e) {
            console.error('BrandingHost cookie initialization fail', e);
        }
    }
    static removebrandingHostCookie(areaIdentifier, env) {
        const windowObject = CookieUtils.getWindowObject();
        try {
            const hostDomain = CookieUtils.getHostDomain(env);
            const domain = CookieUtils.getHostDomain(env);
            if (!areaIdentifier) {
                areaIdentifier = 'na1';
            }
            let brandingHosts = this.getBrandingHostCookie();
            if (!brandingHosts) {
                brandingHosts = {};
            }
            delete brandingHosts[areaIdentifier + hostDomain];
            if (Object.keys(brandingHosts).length === 0) {
                CookieUtils.removeCookie(CookieUtils.KEYS.BRANDING_HOST, {
                    domain,
                    path: '/',
                    samesite: 'None',
                    secure: windowObject.location.protocol === 'https:'
                });
            }
            else {
                CookieUtils.setCookie(CookieUtils.KEYS.BRANDING_HOST, brandingHosts, {
                    domain: CookieUtils.getHostDomain(env),
                    path: '/',
                    samesite: 'None',
                    secure: windowObject.location.protocol === 'https:'
                });
            }
        }
        catch (e) {
            console.error('BrandingHost cookie removal fail', e);
        }
    }
    static clearAllCookies(areaIdentifier, env) {
        for (const key in CookieUtils.KEYS) {
            if (CookieUtils.KEYS.hasOwnProperty(key)) {
                CookieUtils.removeCookie(CookieUtils.KEYS[key], {
                    path: '/'
                });
            }
        }
        CookieUtils.removebrandingHostCookie(areaIdentifier, env);
    }
    static setAuthCookie(data) {
        const now = new Date();
        now.setTime(now.getTime() + 8 * 3600 * 1000);
        data = data.replace(new RegExp('"', 'g'), '\\"');
        console.debug('Setting cookie', CookieUtils.KEYS.AUTH_COOKIE);
        document.cookie = CookieUtils.KEYS.AUTH_COOKIE + '=\"' + data + '\"; expires=' + now.toUTCString() + ';path=/; SameSite=None; Secure';
    }
    static setAccessTokenWithCookieName(cookieName, token, expirationSecs) {
        const expireDate = new Date();
        const windowObject = CookieUtils.getWindowObject();
        expireDate.setSeconds(expireDate.getSeconds() + expirationSecs);
        CookieUtils.setCookie(cookieName, token, {
            domain: this.getDomain(),
            path: '/',
            expires: expireDate,
            samesite: 'None',
            secure: windowObject.location.protocol === 'https:'
        });
    }
}
CookieUtils.KEYS = {
    USER_LAST_ACTIVITY_TIME: 'user_last_activity_time',
    AUTH_COOKIE: 'nice_auth_info',
    BRANDING_HOST: 'brandingHost',
    PASSWORD_EXPIRY_MESSAGE: 'showPasswordExpiringMessage',
    CXONE_TOKEN_COOKIE: 'cxone_token_info',
    TOKEN_EXCHANGE_COOKIE: 'token_exchange'
};
CookieUtils.getSessionId = () => {
    const cookieObj = CookieUtils.getCookieAsObject(CookieUtils.KEYS.AUTH_COOKIE);
    if (cookieObj) {
        return cookieObj.sessionId;
    }
    else {
        return null;
    }
};

class DateConstants {
}
DateConstants.MINUTES_PER_HOUR = 60;
DateConstants.SECONDS_PER_MINUTE = 60;
DateConstants.MILLISECONDS_PER_SECOND = 1000;
DateConstants.MILLISECONDS_PER_MINUTE = 1000 * 60;
DateConstants.HOURS_PER_DAY = 24;
DateConstants.DEFAULT_INTERVAL_SIZE_IN_MINUTES = 15;
DateConstants.TIME_INTERVALS = {
    QUARTER_HOUR: 15,
    HALF_HOUR: 30,
    WHOLE_HOUR: 60
};
DateConstants.DATE_FORMATS = {
    ISO_OFFSET_DATE_TIME: 'YYYY-MM-DDTHH:mm:ssZ',
    ISO_DATE_TIME: 'YYYY-MM-DD[T]HH:mm:ss.SSS[Z]',
    ISO_DATE_TIME_NO_Z: 'YYYY-MM-DD[T]HH:mm:ss.SSS',
    TIME_24: 'HH:mm',
    TIME_NO_LEADING_ZERO_HOUR: 'H:mm',
    US_DATETIME_24: 'MM/dd/yyyy HH:mm',
    DEFAULT_DATE: 'MMM-DD-YYYY',
    DEFAULT_DATE_24: 'MMM-DD-YYYY HH:mm',
    ISO_DATE_NO_TIME: 'YYYY-MM-DD',
    DATE_NO_TIME: 'MMM DD, YYYY'
};
DateConstants.DST = {
    NO_TIMECHANGE: 0,
    SPRING_AHEAD_TIMECHANGE: 1,
    FALL_BEHIND_TIMECHANGE: 2
};
DateConstants.MONTHS = {
    1: 'january',
    2: 'february',
    3: 'march',
    4: 'april',
    5: 'may',
    6: 'june',
    7: 'july',
    8: 'august',
    9: 'september',
    10: 'october',
    11: 'november',
    12: 'december'
};

class StringUtils {
    static capitalize(value) {
        return value.charAt(0).toUpperCase() + value.slice(1);
    }
    static pad(num) {
        return num < 10 ? ('0' + num) : '' + num;
    }
}

class DateUtils {
    static now() {
        return new Date();
    }
    static getISODateWithTZOffset(date) {
        const tzo = -date.getTimezoneOffset();
        const diff = tzo >= 0 ? '+' : '-';
        return (`${date.getFullYear()}-${StringUtils.pad(date.getMonth() + 1)}-${StringUtils.pad(date.getDate())}` +
            `T${StringUtils.pad(date.getHours())}:${StringUtils.pad(date.getMinutes())}:${StringUtils.pad(date.getSeconds())}` +
            `${diff}${StringUtils.pad(Math.floor(Math.abs(tzo / 60)))}:${StringUtils.pad(tzo % 60)}`);
    }
    static clearTime(date) {
        if (!date) {
            date = new Date();
        }
        date.setHours(0);
        date.setMinutes(0);
        date.setSeconds(0);
        date.setMilliseconds(0);
        return date;
    }
    static setTime(date, hour, minute) {
        date.setHours(hour);
        date.setMinutes(minute);
        date.setSeconds(0);
        date.setMilliseconds(0);
        return date;
    }
    static setDateOutOfTimeFormat(timeObj) {
        const hours = timeObj.split(':')[0];
        const minutes = timeObj.split(':')[1];
        const date = new Date();
        date.setHours(parseInt(hours, 10));
        date.setMinutes(parseInt(minutes, 10));
        return date;
    }
    /**
     * Update the date component of a date
     */
    static setDate(dateObject, year, month, date) {
        dateObject.setFullYear(year);
        dateObject.setMonth(month);
        dateObject.setDate(date);
        return dateObject;
    }
    /**
     * Update the date component of a date
     */
    static replaceDateComponent(dateObject, dateObjectToReplaceWith) {
        const replaceDate = dateObjectToReplaceWith.getDate();
        dateObject.setDate(1); // set day of month to 1 to avoid issues where months have different number of days
        dateObject.setFullYear(dateObjectToReplaceWith.getFullYear());
        dateObject.setMonth(dateObjectToReplaceWith.getMonth());
        dateObject.setDate(replaceDate); // now set correct day of month
        return dateObject;
    }
    /**
     * Given two dates how many days separate them
     */
    static daysDiff(start, end) {
        let result = moment(end).startOf('day').diff(moment(start).startOf('day'));
        result =
            result /
                DateConstants.MILLISECONDS_PER_SECOND /
                DateConstants.SECONDS_PER_MINUTE /
                DateConstants.MINUTES_PER_HOUR /
                DateConstants.HOURS_PER_DAY;
        result = Math.round(result);
        return result;
    }
    /**
     * Given two dates how many days separate them.
     * The difference between daysDiff and daysDiffForWeek is that a day is calculated from 00:00:00 till 00:00:00
     * and not from 00:00:00 till 23:59:59.
     */
    static daysDiffIncludingMidnight(start, end) {
        let result = moment(end).subtract(1, 'seconds').startOf('day').diff(moment(start).startOf('day'));
        result =
            result /
                DateConstants.MILLISECONDS_PER_SECOND /
                DateConstants.SECONDS_PER_MINUTE /
                DateConstants.MINUTES_PER_HOUR /
                DateConstants.HOURS_PER_DAY;
        result = Math.round(result);
        return result;
    }
    /**
     * Give two date return the difference in minutes
     *
     */
    static dateDiffInMinutes(startDate, endDate) {
        const diffMs = endDate.getTime() - startDate.getTime();
        return Math.abs(diffMs / 1000 / DateConstants.SECONDS_PER_MINUTE);
    }
    /**
     * Give two date return the difference in hours
     */
    static dateDiffInHours(startDate, endDate) {
        const diffMs = endDate.getTime() - startDate.getTime();
        return diffMs / 1000 / DateConstants.SECONDS_PER_MINUTE / DateConstants.MINUTES_PER_HOUR;
    }
    /**
     * Returns true if each of the supplied date properties match between the two dates given.
     *
     * e.g. "datePartsMatch(aDate, bDate, ['getHours', 'getMinutes']);" returns true if the hours and minutes parts
     * of the supplied dates match.
     *
     */
    static datePartsMatch(aDate, bDate, parts) {
        return parts.reduce((total, currentValue) => {
            /* eslint no-useless-call: "off" */
            total = total && aDate[currentValue].call(aDate) === bDate[currentValue].call(bDate);
            return total;
        }, true);
    }
    static hoursAndMinutesMatch(aDate, bDate) {
        const fns = ['getHours', 'getMinutes'];
        return DateUtils.datePartsMatch(aDate, bDate, fns);
    }
    static daysMatch(aDate, bDate) {
        const fns = ['getFullYear', 'getMonth', 'getDate'];
        return DateUtils.datePartsMatch(aDate, bDate, fns);
    }
    static isValidTimeInterval(timeInterval) {
        let isValid = false;
        const validValues = Object.values(DateConstants.TIME_INTERVALS);
        validValues.forEach((validValue) => {
            if (validValue === timeInterval) {
                isValid = true;
            }
        });
        return isValid;
    }
    /**
     * Is the date within a range of dates
     */
    static isDateWithin(searchDate, startDate, endDate) {
        return searchDate.getTime() >= startDate.getTime() && searchDate.getTime() <= endDate.getTime();
    }
    /**
     * Returns a new date such that the time corresponds to the latest time in 15 minute increments that is earlier
     * than or equal to the time of the supplied date.
     */
    static floorTime(date, timeInterval) {
        const dateTime = new Date(date);
        if (!(timeInterval && timeInterval <= DateConstants.MINUTES_PER_HOUR)) {
            timeInterval = DateConstants.DEFAULT_INTERVAL_SIZE_IN_MINUTES;
        }
        const minutes = dateTime.getMinutes();
        const minutesDivStep = Math.floor(minutes / timeInterval);
        const minutesModStep = minutes % timeInterval;
        if (minutesModStep > 0) {
            if (minutes > timeInterval) {
                dateTime.setMinutes(minutesDivStep * timeInterval);
            }
            else {
                dateTime.setMinutes(0);
            }
        }
        return dateTime;
    }
    /**
     * Returns a new date such that the time corresponds to the earliest time in 15 minute increments that is later
     * than or equal to the time of the supplied date.
     */
    static ceilTime(date, timeInterval) {
        const dateTime = new Date(date);
        if (!(timeInterval && timeInterval <= DateConstants.MINUTES_PER_HOUR)) {
            timeInterval = DateConstants.DEFAULT_INTERVAL_SIZE_IN_MINUTES;
        }
        const minutes = dateTime.getMinutes();
        const minutesDivStep = Math.floor(minutes / timeInterval);
        const minutesModStep = minutes % timeInterval;
        if (minutesModStep > 0) {
            if ((minutesDivStep + 1) * timeInterval > 59) {
                dateTime.setMinutes(0);
                dateTime.setHours(dateTime.getHours() + 1);
            }
            else {
                dateTime.setMinutes((minutesDivStep + 1) * timeInterval);
            }
        }
        return dateTime;
    }
    static roundTimeByStep(date, timeInterval) {
        const dateTime = new Date(date);
        if (!(timeInterval && timeInterval <= DateConstants.MINUTES_PER_HOUR)) {
            timeInterval = DateConstants.DEFAULT_INTERVAL_SIZE_IN_MINUTES;
        }
        const minutes = dateTime.getMinutes();
        const minutesModStep = minutes % timeInterval;
        const halfStep = timeInterval / 2;
        if (minutesModStep > 0) {
            if (minutesModStep > halfStep - 1) {
                return DateUtils.ceilTime(dateTime, timeInterval);
            }
            return DateUtils.floorTime(dateTime, timeInterval);
        }
        return dateTime;
    }
    /**
     * Returns an array of times representing the time range between the startDate and endDate in increments as
     * given by the timeInterval param.
     *
     * @param includePartialIntervals when true, include partial intervals (the end time or later) even if it
     * doesn't line up with the given timeInterval.
     */
    static getIntervalsBetween(startDate, endDate, timeInterval, includePartialIntervals) {
        const endMoment = moment(endDate);
        let intervalSize = DateConstants.DEFAULT_INTERVAL_SIZE_IN_MINUTES;
        const intervals = [];
        let currentMoment = moment(startDate);
        const includePartials = !includePartialIntervals ? false : includePartialIntervals;
        if (DateUtils.isValidTimeInterval(timeInterval)) {
            intervalSize = timeInterval;
        }
        while (currentMoment.isBefore(endMoment) || currentMoment.isSame(endMoment)) {
            intervals.push(currentMoment.toDate());
            currentMoment = moment(currentMoment).add(intervalSize, 'minutes');
        }
        if (includePartials && moment(intervals[intervals.length - 1]).isBefore(endMoment)) {
            intervals.push(moment(intervals[intervals.length - 1])
                .add(intervalSize, 'minutes')
                .toDate());
        }
        return intervals;
    }
    static isCrossMidnight(startTime, endTime) {
        return moment(startTime).dayOfYear() !== moment(endTime).dayOfYear();
    }
    static isCrossNoon(startTime, endTime) {
        const startMoment = moment(startTime);
        const endMoment = moment(endTime);
        const dayDiff = endMoment.dayOfYear() - startMoment.dayOfYear();
        let result = false;
        if (dayDiff === 0) {
            result = startMoment.hour() < 12 && endMoment.hour() >= 12;
        }
        else if (Math.abs(dayDiff) > 1) {
            result = true;
        }
        else if (Math.abs(dayDiff) === 1) {
            if (startMoment.hour() < 12) {
                result = true;
            }
            else if (startMoment.hour() >= 12 && endMoment.hour() < 12) {
                result = false;
            }
            else if (startMoment.hour() >= 12 && endMoment.hour() >= 12) {
                result = true;
            }
        }
        return result;
    }
    static isBetweenInclusive(date, startDate, endDate) {
        const d = moment(date);
        if (d.isSame(startDate) || d.isSame(endDate)) {
            return true;
        }
        else if (d.isBefore(endDate) && d.isAfter(startDate)) {
            return true;
        }
        return false;
    }
    static isSameOrAfter(date1, date2) {
        const moment1 = moment(date1);
        const moment2 = moment(date2);
        return moment1.isSame(moment2) || moment1.isAfter(moment2);
    }
    static isSameOrBefore(date1, date2) {
        const moment1 = moment(date1);
        const moment2 = moment(date2);
        return moment1.isSame(moment2) || moment1.isBefore(moment2);
    }
    /**
     * Returns a new date such that the time corresponds to the received parameter (expected format is: '8:00 AM')
     *
     * @param timeString (expected format is: '8:00 AM')
     */
    static calculateExactTwelve(parts) {
        if (parts[1] !== '12') {
            return /am/i.test(parts[3]) ? parseInt(parts[1], 10) : parseInt(parts[1], 10) + 12;
        }
        return /am/i.test(parts[3]) ? 0 : parseInt(parts[1], 10);
    }
    static getDateFromTimeString(timeString) {
        const d = new Date();
        const s = timeString;
        const parts = s.match(/(\d+)\:(\d+) (\w+)/);
        const hours = DateUtils.calculateExactTwelve(parts);
        const minutes = parseInt(parts[2], 10);
        d.setHours(hours);
        d.setMinutes(minutes);
        d.setSeconds(0);
        return d;
    }
    static formatAsUTC(date, timezone) {
        let utcString;
        if (!timezone) {
            timezone = moment.tz.guess();
        }
        utcString = moment(date).format(DateConstants.DATE_FORMATS.ISO_DATE_TIME_NO_Z);
        utcString = moment.tz(utcString, timezone).utc().format(DateConstants.DATE_FORMATS.ISO_DATE_TIME);
        return utcString;
    }
    static calcDays(startDate, endDate) {
        const endDateConverted = new Date(endDate);
        const retVal = [];
        let current = new Date(startDate);
        while (current <= endDateConverted) {
            retVal.push(new Date(current));
            current = moment(current).add(1, 'days').toDate();
        }
        return retVal;
    }
    static getEndOfDay(date) {
        return moment(date).endOf('day').toDate();
    }
    static getMinuteDifferenceOfOffset(sourceOffset, destinationOffset) {
        let minDiff;
        if (sourceOffset === destinationOffset) {
            return 0;
        }
        if (sourceOffset * destinationOffset > 0) {
            // If both offsets are negative
            if (sourceOffset < 0) {
                minDiff = Math.abs(sourceOffset) - Math.abs(destinationOffset);
            }
            else {
                minDiff = (Math.abs(sourceOffset) - Math.abs(destinationOffset)) * -1;
            }
        }
        else {
            minDiff = Math.abs(sourceOffset) + Math.abs(destinationOffset);
            if (destinationOffset < 0) {
                minDiff *= -1;
            }
        }
        return minDiff;
    }
    static convertDateFromOneOffsetToAnother(date, fromOffset, toOffset) {
        const momentDate = moment(date);
        const minutediff = DateUtils.getMinuteDifferenceOfOffset(fromOffset, toOffset);
        momentDate.add(minutediff, 'minutes');
        return momentDate.toDate();
    }
    static set4DigitTimeFormat(timeObj) {
        const hours = parseInt(timeObj.split(':')[0], 10);
        const minutes = timeObj.split(':')[1];
        return hours + ':' + minutes;
    }
    // format of date '2016-06-01', timeStr: 17:00
    static convertRangeOfDatesToTimeStampArray(startDateStr, endDateStr, timeStr, dateStrFormat) {
        timeStr = timeStr || '00:00';
        let startDate = moment(startDateStr + ' ' + timeStr);
        const endDate = moment(endDateStr + ' ' + timeStr);
        const diffDays = endDate.diff(startDate, 'days') + 1;
        const timeStampArray = [];
        for (let i = 0; i < diffDays; i += 1) {
            timeStampArray.push({ timeStamp: startDate.valueOf(), dateStr: startDate.format(dateStrFormat) });
            startDate = startDate.add(1, 'days');
        }
        return timeStampArray;
    }
    // example format- PT3H30M -->3:30 or PT2M --> 0:02
    static getPatternTimeFromServerAndConvertIntoHoursMinutes(patternTime) {
        let parts = patternTime.slice(2).replace('M', '').split('H');
        if (parts.length > 1) {
            if (!parts[1]) {
                parts[1] = 0;
            }
        }
        else {
            parts = [0, parts[0]];
        }
        parts[0] = parseInt(parts[0], 10);
        parts[1] = parseInt(parts[1], 10);
        parts[1] = parts[1] < 10 ? '0' + parts[1] : parts[1];
        return parts[0] + ':' + parts[1];
    }
    static getLocalDateWithSameTimezoneTime(timezone, date) {
        date = date || new Date();
        return new Date(moment(date).tz(timezone).format().slice(0, 19));
    }
    /**
     * returns the start & end of the day for given timezone
     * the date object we receive is the date we working on
     * if we got (Aug 10th 2:00AM) in (-5:00 US TZ) --> in UTC this is Aug 9th, but we will work on 10th.
     * so given date=(Aug 10th 2:00AM) & timezone=(Asia/Jerusalem) as parameters
     * (& working on (-5:00 US TZ) machine - this should not matter)
     * -->  the output will be: {startOfTheDay: "2017-08-09T21:00:00+00:00", endOfTheDay: "2017-08-10T20:59:59+00:00"}
     * because Asia/Jerusalem timezone is UTC+3:00
     *
     * @param date - js date object
     * @param timezone - string supported by moment "Asia/Jerusalem" / "America/New_York" ...
     * @param format - moment date format
     * @returns  - start and end of the day of the TZ param in UTC time
     */
    static getStartAndEndOfTheDayInUTCByTimezone(date, timezone, format) {
        format = format || 'YYYY-MM-DDTHH:mm:ssZ';
        const _m = moment(date);
        const localOffset = moment.tz(moment.tz.guess()).utcOffset();
        const tzOffset = moment.tz(timezone).utcOffset();
        let tzDate;
        // the date we got in the date param - this is the date we want to find Start and End of the day for.
        // so we are adding the local TZ offset
        if (localOffset > 0) {
            tzDate = _m.subtract(localOffset, 'minutes').tz(timezone);
        }
        else if (localOffset < 0) {
            tzDate = _m.add(localOffset, 'minutes').tz(timezone);
        }
        else {
            // === 0
            tzDate = _m.tz(timezone);
        }
        // then we are setting the offset for the requested timezone
        if (tzOffset < 0) {
            tzDate = _m.subtract(tzOffset, 'minutes').tz(timezone);
        }
        else if (tzOffset > 0) {
            tzDate = _m.add(tzOffset, 'minutes').tz(timezone);
        }
        else {
            // === 0
            tzDate = _m.tz(timezone);
        }
        const utcStartDayTimeBySu = tzDate.clone().startOf('day').utc();
        const utcEndDayTimeBySu = tzDate.clone().endOf('day').utc();
        return {
            startOfTheDay: utcStartDayTimeBySu.format(format),
            endOfTheDay: utcEndDayTimeBySu.format(format)
        };
    }
    /*
     * converts moment.js date format format to Highcharts date format (based on php date format)
     */
    static convertMomentFormatToHighchartsFormat(momentFormat) {
        if (typeof momentFormat !== 'string') {
            return momentFormat;
        }
        return momentFormat
            .replace(/H/g, '%G')
            .replace(/%G%G/g, '%H')
            .replace(/h/g, '%g')
            .replace(/%g%g/g, '%h') // hour
            .replace(/A/g, '%A')
            .replace(/a/g, '%a') // am/pm
            .replace(/mm/g, '%i') // minutes
            .replace(/ss/g, '%s') // seconds
            .replace(/SSS/g, '%v') // milliseconds
            .replace(/DD/g, '%d')
            .replace(/D/g, '%e') // date
            .replace(/dddd/g, '%l')
            .replace(/ddd/g, '%D') // day
            .replace(/MMMM/g, '%B')
            .replace(/MMM/g, '%b')
            .replace(/MM/g, '%m') // month
            .replace(/Y/g, '%Y')
            .replace(/%Y%Y%Y%Y/g, '%Y')
            .replace(/%Y%Y/g, '%y'); // year
    }
    /**
     * It applies the ASP.NET format date. That format uses the UnixEpochTime,
     * in other words we have to return the number of milliseconds since 1 January 1970 00:00:00.
     * For example this static can return: "/Date(1528349564843)/"
     * {date}   -->     JavaScript Date object         *
     */
    static applyAspNetDateFormat(date) {
        return '/Date(' + date.getTime() + ')/';
    }
    /**
     * It parses the ASP.NET format date in order to retrieve a JavaScript Date object.
     * {string}   -->     JavaScript string ("/Date(1528349564843)/") or ("/Date(1528349564843-0600)/")
     *
     * Note:
     * If you send a wrong date format, you are going to retrieve 1 January 1970 00:00:00 in UTC.
     *
     * Here we can find more details:
     * https://stackoverflow.com/questions/206384/how-do-i-format-a-microsoft-json-date
     */
    static parseAspNetDateFormat(value) {
        const aspNetPattern = /\/Date\((\d+)(?:([+-])(\d\d)(\d\d))?\)\//;
        const resultArray = value.match(aspNetPattern);
        let unixEpochTime = 0;
        if (resultArray) {
            unixEpochTime = parseInt(resultArray[1], 10);
            // To handle offset cases like this one: ("/Date(1528349564843-0600)/")
            if (resultArray[2] && resultArray[3] && resultArray[4]) {
                const arithmeticSign = resultArray[2] === '+' ? -1 : 1;
                const offsetHours = parseInt(resultArray[3], 10);
                const offsetMinutes = parseInt(resultArray[4], 10);
                unixEpochTime += arithmeticSign * (offsetHours * 60 + offsetMinutes) * 60 * 1000;
            }
        }
        return new Date(unixEpochTime);
    }
    /**
     * It returns a JavaScript object with the startDateTime and endDateTime.
     * {presentDateTime}    -->     The present DateTime to be used to create the range.
     * {numberOfDays}       -->     The number of days of the range.
     * {fromPresentToPast}  -->     A boolean to indicate the direction to be used in order to create the range.
     * true    -->     From present to past.
     * false   -->     From present to future.
     */
    static getDateTimeRange(presentDateTime, numberOfDays, fromPresentToPast) {
        const startMoment = moment(presentDateTime);
        const endMoment = moment(presentDateTime);
        if (numberOfDays > 0) {
            if (fromPresentToPast) {
                startMoment.subtract(numberOfDays, 'days');
                endMoment.subtract(1, 'days');
            }
            else {
                startMoment.add(1, 'days');
                endMoment.add(numberOfDays, 'days');
            }
        }
        return {
            startDateTime: DateUtils.clearTime(startMoment.toDate()),
            endDateTime: DateUtils.getEndOfDay(endMoment.toDate())
        };
    }
    static changeUTCToLocaleDateString(dateTime, dateFormat) {
        const dateTimeInUtc = moment.utc(dateTime);
        const localDateTime = dateTimeInUtc.local();
        return localDateTime.format(dateFormat);
    }
    static getWeekDays(translationKeys) {
        const weekDaysList = [];
        translationKeys = translationKeys || {};
        weekDaysList.push(translationKeys.monday ? translationKeys.monday : 'Monday');
        weekDaysList.push(translationKeys.tuesday ? translationKeys.tuesday : 'Tuesday');
        weekDaysList.push(translationKeys.wednesday ? translationKeys.wednesday : 'Wednesday');
        weekDaysList.push(translationKeys.thursday ? translationKeys.thursday : 'Thursday');
        weekDaysList.push(translationKeys.friday ? translationKeys.friday : 'Friday');
        weekDaysList.push(translationKeys.saturday ? translationKeys.saturday : 'Saturday');
        weekDaysList.push(translationKeys.sunday ? translationKeys.sunday : 'Sunday');
        return weekDaysList;
    }
    static getHourList() {
        const hourList = [];
        for (let i = 1; i < 25; i += 1) {
            if (i === 1) {
                hourList.push({ value: i, name: i });
            }
            else {
                hourList.push({ value: i, name: i });
            }
        }
        return hourList;
    }
    static getHourListWithParams(maxHours, increment) {
        const hourList = [];
        for (let i = 1; i < maxHours + increment; i += increment) {
            hourList.push(i.toString());
        }
        return hourList;
    }
    static convertTimeToHoursLength(timeStr) {
        // expect string time like 12:30
        const timeParts = timeStr.split(':');
        const minutes = parseInt(timeParts[1], 10);
        const hours = parseInt(timeParts[0], 10);
        if (minutes > 0) {
            return hours + minutes / 60;
        }
        return hours;
    }
    static convertTimeToMinutesLength(time) {
        const parts = time.split(':');
        if (parts.length !== 2) {
            return 0;
        }
        const hours = parseInt(parts[0], 10);
        const minutes = parseInt(parts[1], 10);
        if (isNaN(hours) || isNaN(minutes)) {
            return 0;
        }
        return hours * 60 + minutes;
    }
    static convertMinutesLengthToTime(minutesLength) {
        const hours = Math.floor(minutesLength / 60);
        const minutes = minutesLength % 60;
        let minutesString = '' + minutes;
        let hoursString = '' + hours;
        if (minutes < 10) {
            minutesString = '0' + minutes;
        }
        if (hours < 10) {
            hoursString = '0' + hours;
        }
        return `${hoursString}:${minutesString}`;
    }
    static getTimeList(startTime, lengthOfList, minutesIncrement) {
        const times = [];
        let currentTime = 0;
        if (typeof startTime !== 'string') {
            startTime = '0:00';
        }
        if (!/^([0-1]?[0-9]|2[0-3])(:[0-5][0-9])?$/.test(startTime)) {
            return null;
        }
        const startTimeParts = startTime.split(':');
        currentTime += parseInt(startTimeParts[0], 10) * 60;
        currentTime += parseInt(startTimeParts[1], 10);
        // loop to increment the time and push results in array
        for (let i = 0; i < lengthOfList; i += 1) {
            const hour = Math.floor(currentTime / 60);
            const minute = currentTime % 60;
            times.push(('' + hour).slice(-2) + ':' + ('0' + minute).slice(-2));
            currentTime += minutesIncrement;
        }
        return times;
    }
    static convertTime24To12(time) {
        let timeArray = time.match(/^([01]\d|2[0-3])(:)([0-5]\d)?$/) || [time];
        if (timeArray.length > 1) {
            timeArray = timeArray.slice(1);
            timeArray[3] = +timeArray[0] < 12 ? 'am' : 'pm';
            timeArray[0] = +timeArray[0] % 12 || 12;
        }
        return timeArray.join('');
    }
    static isISO8601Date(dateString) {
        const regexIso8601 = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(.\d{1,3})?)?(Z|(-|\+)\d{2}:\d{2})/;
        const match = dateString.match(regexIso8601);
        return match !== null;
    }
}

class ObjectUtils {
    static parseDotNotation(stringToParse, value, obj) {
        let currentObj = obj;
        const keys = stringToParse.split('.');
        let i;
        const l = Math.max(1, keys.length - 1);
        let key;
        for (i = 0; i < l; ++i) {
            key = keys[i];
            currentObj[key] = currentObj[key] || {};
            currentObj = currentObj[key];
        }
        currentObj[keys[i]] = value;
        delete obj[stringToParse];
    }
    static isArray(arr) {
        return Array.isArray(arr) || arr instanceof Array;
    }
    static isObject(value) {
        return value !== null && typeof value === 'object';
    }
    static isDate(value) {
        return toString.call(value) === '[object Date]';
    }
    static isFunction(value) {
        return typeof value === 'function';
    }
    static serializeValue(v) {
        if (ObjectUtils.isObject(v)) {
            return ObjectUtils.isDate(v) ? v.toISOString() : JSON.stringify(v);
        }
        return v;
    }
    static forEachSorted(obj, iterator, context) {
        const keys = Object.keys(obj).sort();
        for (const key of keys) {
            iterator.call(context, obj[key], key);
        }
        return keys;
    }
    static hasAllKeys(requiredKeys, objectToCheck) {
        return requiredKeys.every((item) => objectToCheck.hasOwnProperty(item));
    }
    static cloneDeep(target) {
        if (target === null) {
            return target;
        }
        if (target instanceof Date) {
            return new Date(target.getTime());
        }
        if (target instanceof Array) {
            const cp = [];
            target.forEach((v) => {
                cp.push(v);
            });
            return cp.map((n) => ObjectUtils.cloneDeep(n));
        }
        if (typeof target === 'object' && Object.keys(target).length > 0) {
            const cp = Object.assign({}, target);
            Object.keys(cp).forEach((k) => {
                cp[k] = ObjectUtils.cloneDeep(cp[k]);
            });
            return cp;
        }
        return target;
    }
    static pick(obj, ...keys) {
        const ret = {};
        keys.forEach((key) => {
            ret[key] = obj[key];
        });
        return ret;
    }
}

class LocalStorageUtils {
    static getWindowObject() {
        return window;
    }
    static setStorageMode(mode) {
        if (mode === this.LOCAL_STORAGE_MODE || mode === this.SESSION_STORAGE_MODE) {
            this.storageMode = mode;
        }
    }
    static getStorageMode() {
        return this.storageMode;
    }
    static isStorageAvailable() {
        try {
            const key = '___storagetest___';
            if (this.storageMode === this.LOCAL_STORAGE_MODE) {
                this.getWindowObject().localStorage.setItem(key, 'testing123');
                this.getWindowObject().localStorage.removeItem(key);
            }
            else {
                this.getWindowObject().sessionStorage.setItem(key, 'testing123');
                this.getWindowObject().sessionStorage.removeItem(key);
            }
            return true;
        }
        catch (e) {
            return false;
        }
    }
    static buildKeyWithPrefix(key) {
        return `${LocalStorageUtils.prefix}.${key}`;
    }
    static getItem(key) {
        const item = this.storageMode === this.LOCAL_STORAGE_MODE ?
            this.getWindowObject().localStorage.getItem(LocalStorageUtils.buildKeyWithPrefix(key)) :
            this.getWindowObject().sessionStorage.getItem(LocalStorageUtils.buildKeyWithPrefix(key));
        try {
            return JSON.parse(item);
        }
        catch (_a) {
            return item;
        }
    }
    static setItem(key, value) {
        const stringValue = ObjectUtils.isObject(value) || ObjectUtils.isFunction(value)
            ? JSON.stringify(value) : (value ? value.toString() : '');
        this.storageMode === this.LOCAL_STORAGE_MODE ?
            this.getWindowObject().localStorage.setItem(LocalStorageUtils.buildKeyWithPrefix(key), stringValue) :
            this.getWindowObject().sessionStorage.setItem(LocalStorageUtils.buildKeyWithPrefix(key), stringValue);
    }
    static removeItem(key) {
        this.storageMode === this.LOCAL_STORAGE_MODE ?
            this.getWindowObject().localStorage.removeItem(LocalStorageUtils.buildKeyWithPrefix(key)) :
            this.getWindowObject().sessionStorage.removeItem(LocalStorageUtils.buildKeyWithPrefix(key));
    }
    static getKeys() {
        return this.storageMode === this.LOCAL_STORAGE_MODE ?
            Object.keys(this.getWindowObject().localStorage) :
            Object.keys(this.getWindowObject().sessionStorage);
    }
    static clearAllValues() {
        for (const key in this.KEYS) {
            if (this.KEYS.hasOwnProperty(key)) {
                LocalStorageUtils.removeItem(this.KEYS[key]);
            }
        }
    }
    static clearEntireStorage() {
        this.getWindowObject().localStorage.clear();
        this.getWindowObject().sessionStorage.clear();
    }
    static getStatusFilters(key) {
        const statusFilters = LocalStorageUtils.getItem(this.PERSISTENT_KEYS.statusFilters);
        return statusFilters ? statusFilters[key] : undefined;
    }
    static setStatusFilters(key, value) {
        const statusFilters = LocalStorageUtils.getItem(this.PERSISTENT_KEYS.statusFilters) === null ? {} :
            LocalStorageUtils.getItem(this.PERSISTENT_KEYS.statusFilters);
        statusFilters[key] = value;
        return LocalStorageUtils.setItem(this.PERSISTENT_KEYS.statusFilters, statusFilters);
    }
    static getUserTokenExpirationTime() {
        return moment(LocalStorageUtils.getItem(this.KEYS.expirationTime));
    }
    static getAccessTokenExpirationTime() {
        return LocalStorageUtils.getItem(this.KEYS.accessTokenExpirationTime);
    }
    static setAccessTokenExpirationTime() {
        const epoch = moment().add(CookieUtils.getAccessTokenExpirationTime(), 'seconds').valueOf();
        LocalStorageUtils.setItem(this.KEYS.accessTokenExpirationTime, epoch);
    }
    static setAccessTokenExpirationTimeFromResponse(expirationTime) {
        const epoch = moment().add(expirationTime, 'seconds').valueOf();
        LocalStorageUtils.setItem(this.KEYS.accessTokenExpirationTime, epoch);
    }
}
LocalStorageUtils.LOCAL_STORAGE_MODE = 'local';
LocalStorageUtils.SESSION_STORAGE_MODE = 'session';
LocalStorageUtils.prefix = 'wfo_saas';
LocalStorageUtils.storageMode = LocalStorageUtils.LOCAL_STORAGE_MODE;
LocalStorageUtils.KEYS = {
    accessTokenExpirationTime: 'accessTokenExpirationTime',
    brandingProfile: 'brandingProfile',
    expirationTime: 'userTokenExpirationTime',
    permissions: 'userPermissionsKey',
    samlInfo: 'saml_info',
    scope: 'scope',
    sessionState: 'sessionState',
    toastrManagerTimeOut: 'toastrManagerTimeOut',
    userApplications: 'userApplications',
    userDetails: 'userDetails',
    userRefreshToken: 'userRefreshToken',
    userToken: 'userToken',
    encryptedToken: 'appInfo',
    encryptedRToken: 'appRInfo',
    encryptedKey: 'sk',
    userInfo: 'userInfo'
};
/*
Persistent Keys are not cleared
 */
LocalStorageUtils.PERSISTENT_KEYS = {
    studioDoNotShow: 'studioDoNotShow',
    statusFilters: 'statusFilters',
    niceSessionLogs: 'niceSessionLogs'
};

class User {
    static get instance() {
        if (!this._instance) {
            this._instance = new User();
            this._instance.reset();
        }
        return this._instance;
    }
    reset() {
        this._user = {
            assignedGroup: '',
            emailAddress: '',
            firstName: '',
            hireDate: '',
            id: '',
            impersonated: false,
            invitationExpired: false,
            loggedIn: false,
            lastName: '',
            organizationName: '',
            status: '',
            token: '',
            refreshToken: '',
            userName: '',
            role: ''
        };
    }
    get assignedGroup() {
        return this._user.assignedGroup;
    }
    get emailAddress() {
        return this._user.emailAddress;
    }
    get firstName() {
        return this._user.firstName;
    }
    get fullName() {
        return `${StringUtils.capitalize(this._user.firstName)} ${StringUtils.capitalize(this._user.lastName)}`.trim();
    }
    get hireDate() {
        return this._user.hireDate;
    }
    get id() {
        return this._user.id;
    }
    get invitationExpired() {
        return this._user.invitationExpired;
    }
    get impersonated() {
        return this._user.impersonated;
    }
    get loggedIn() {
        return this._user.loggedIn;
    }
    get lastName() {
        return this._user.lastName;
    }
    get organizationName() {
        return this._user.organizationName;
    }
    get status() {
        return this._user.status;
    }
    get role() {
        return this._user.role;
    }
    get token() {
        return this._user.token ? this._user.token : '';
    }
    set token(value) {
        this._user.token = value;
    }
    get refreshToken() {
        return this._user.refreshToken ? this._user.refreshToken : '';
    }
    set refreshToken(value) {
        this._user.refreshToken = value;
    }
    get user() {
        return this._user;
    }
    set user(user) {
        this._user = user;
    }
    get userName() {
        return this._user.userName;
    }
}

class CoreUtils {
    static isTokenExpired() {
        // Date example in local storage "2020-01-28T19:58:25-06:00"
        const userTokenExpirationTime = LocalStorageUtils.getItem(LocalStorageUtils.KEYS.expirationTime);
        let expirationTime;
        if (userTokenExpirationTime) {
            expirationTime = new Date(userTokenExpirationTime).getTime();
            return expirationTime < DateUtils.now().getTime();
        }
        return undefined;
    }
    static isRefreshTokenExpired() {
        // Date example in local storage "2020-01-28T19:58:25-06:00"
        const refreshToken = LocalStorageUtils.getItem(LocalStorageUtils.KEYS.encryptedToken) ? User.instance.refreshToken : LocalStorageUtils.getItem(LocalStorageUtils.KEYS.userRefreshToken);
        const claims = CoreUtils.getClaimsFromToken(refreshToken);
        if (claims && claims.exp) {
            return new Date(claims.exp).getTime() < DateUtils.now().getTime();
        }
        return undefined;
    }
    static isValidJWT(jwtToken) {
        return this.JWT_REGEX.test(jwtToken);
    }
    static getClaimsFromToken(token) {
        if (!CoreUtils.isValidJWT(token)) {
            return null;
        }
        try {
            const encodedBase64Url = token.split('.')[1];
            const encodedBase64 = encodedBase64Url.replace('-', '+').replace('_', '/');
            return JSON.parse(atob(encodedBase64));
        }
        catch (e) {
            console.debug('Retrying with atobV2 as error in getClaimsFromToken due to: ' + e);
            return JSON.parse(CoreUtils.atobV2(token));
        }
    }
    static encodeToBase64(buffer) {
        let binary = '';
        const bytes = new Uint8Array(buffer);
        const len = bytes.byteLength;
        for (let i = 0; i < len; i += 1) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    }
    static decodeFromBase64(buffer) {
        let binary = '';
        const bytes = new Uint8Array(buffer);
        const len = bytes.byteLength;
        for (let i = 0; i < len; i += 1) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.atob(binary);
    }
    static encodeStrToBase64(str) {
        return CoreUtils.encodeToBase64(new TextEncoder().encode(str));
    }
    static decodeStrFromBase64(str) {
        return CoreUtils.decodeFromBase64(new TextEncoder().encode(str));
    }
    static getRoundedNumber(num) {
        if (num % 1 === 0.5) {
            return num;
        }
        return Math.round(num);
    }
    static s4() {
        return Math.floor((1 + Math.random()) * 0x10000)
            .toString(16)
            .substring(1);
    }
    static getUserInitials(user) {
        if (user == null) {
            return '';
        }
        return (user.firstName && user.firstName.length > 0 ?
            user.firstName.substring(0, 1).toUpperCase() : '') +
            (user.lastName && user.lastName.length > 0 ? user.lastName.substring(0, 1).toUpperCase() : '');
    }
    static getUserName(user) {
        if (!user) {
            return '';
        }
        return `${StringUtils.capitalize(user.firstName || '')} ${StringUtils.capitalize(user.lastName || '')}`.trim();
    }
    static generateUUID() {
        return `${CoreUtils.s4()}${CoreUtils.s4()}-${CoreUtils.s4()}-${CoreUtils.s4()}-` +
            `${CoreUtils.s4()}-${CoreUtils.s4()}${CoreUtils.s4()}${CoreUtils.s4()}`;
    }
    static getNestedProperty(obj, propertyName) {
        return propertyName.split('.').reduce((memo, prop) => {
            return memo[prop];
        }, obj);
    }
    static setNestedProperty(obj, propertyName, value) {
        const props = propertyName.split('.');
        props.reduce((memo, prop, index) => {
            if (index === props.length - 1) {
                memo[prop] = value;
            }
            else if (memo[prop] === undefined) {
                memo[prop] = {};
            }
            return memo[prop];
        }, obj);
    }
    /**
     * Check string for expression, default use is to check for special characters
     * @param input - string to inspect
     * @param expression - regex to inspect with
     */
    static checkRegExValidation(input, expression) {
        const regex = expression || /^[^/!\\+<>?#&,%"]*$/;
        if (regex instanceof RegExp) {
            return regex.test(input);
        }
        else {
            return false;
        }
    }
    /**
     * Format/converts bytes into readable format with appropriate unit
     *
     * @param integer bytes     Number of bytes to format
     * @param integer decimals  Number of digits after the decimal
     */
    static formatBytes(bytes, decimals) {
        const numBytes = parseInt(bytes, 10) || 0;
        if (numBytes === 0) {
            return '0 Bytes';
        }
        const unit = 1024;
        const precision = decimals ? decimals <= 0 ? 0 : decimals || 2 : 0;
        const unitNames = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        const temp = Math.floor(Math.log(numBytes) / Math.log(unit));
        return parseFloat((numBytes / Math.pow(unit, temp)).toFixed(precision)) + ' ' + unitNames[temp];
    }
    static isNil(value) {
        if (typeof value === 'undefined') {
            return true;
        }
        if (value === null) {
            return true;
        }
        return false;
    }
    static numberFormatWithCommas(num) {
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    /***
     * @str is a string
     * @return hashCode
     * */
    static stringToHashCode(str) {
        let hash = 0;
        let i;
        let chr;
        if (str.length === 0) {
            return hash;
        }
        for (i = 0; i < str.length; i += 1) {
            chr = str.charCodeAt(i);
            // tslint:disable:no-bitwise
            // eslint-disable-next-line no-bitwise
            hash = ((hash << 5) - hash) + chr;
            // eslint-disable-next-line no-bitwise
            hash |= 0; // Convert to 32bit integer
        }
        return hash;
    }
    /**
     * Delta of users who were added to group and users who were deleted from group
     * E.g. only deltas will be sent to server eventually
     *
     */
    static createDeltas(group, employees, newUser) {
        if (newUser) {
            group.deltaAdded = employees.selectedUsers;
            group.deltaDeleted = null;
        }
        else {
            let delta = [];
            group.deltaAdded = employees.groupUsers.reduce((accumulator, currentValue) => {
                if (!ArrayUtils.includes(employees.origGroupUsers, currentValue)) {
                    accumulator.push(currentValue);
                }
                return accumulator;
            }, delta);
            delta = [];
            group.deltaDeleted = employees.origGroupUsers.reduce((accumulator, currentValue) => {
                if (!ArrayUtils.includes(employees.groupUsers, currentValue)) {
                    accumulator.push(currentValue);
                }
                return accumulator;
            }, delta);
        }
        return group;
    }
    static deepCopy(target) {
        return ObjectUtils.cloneDeep(target);
    }
    /**
     * Alias for deepCopy
     */
    static cloneDeep(target) {
        return ObjectUtils.cloneDeep(target);
    }
    static pick(obj, ...keys) {
        return ObjectUtils.pick(obj, ...keys);
    }
    static copyTextToClipboard(text) {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.setAttribute('readonly', '');
        textArea.classList.add('text-area-clipboard-helper');
        document.body.appendChild(textArea);
        textArea.select();
        let successful = false;
        try {
            successful = document.execCommand('copy');
        }
        catch (err) {
            console.error('Fallback: Oops, unable to copy', err);
        }
        document.body.removeChild(textArea);
        return successful;
    }
    static invokePromiseWithTimeout(promise, msTimeout = this.PROMISE_DEFAULT_TIMEOUT) {
        const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), msTimeout));
        return Promise.race([promise, timeout]);
    }
    static atobV2(base64Str) {
        const base64Url = base64Str.indexOf('.') > -1 ? base64Str.split('.')[1] : base64Str.split('.')[0];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        return decodeURIComponent(window.atob(base64).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
    }
    static getDelimitedArray(input, customSeparators) {
        if (customSeparators && customSeparators.length > 0) {
            const regex = /[-\/\\^$*+?.()|[\]{}]/g; //  pattern for special character eg: -,\,//,^,$,*,+,?,.,{,},|,(,),[,]
            const escapedSeparators = customSeparators.map(separator => {
                return separator.replace(regex, '\\$&'); // Escape special characters
            });
            const separatorsPattern = escapedSeparators.join('|'); // Combine escaped separators
            const separators = new RegExp(separatorsPattern); // Create regex from custom separators
            // Remove empty items after trimming
            return input
                .split(separators) // Split input using custom separators
                .map(item => item.trim()) // Trim leading and trailing whitespaces
                .filter(item => item !== '');
        }
        return [input];
    }
}
CoreUtils.JWT_REGEX = /^[a-zA-Z0-9\-_]+?\.[a-zA-Z0-9\-_]+?\.([a-zA-Z0-9\-_]+)?$/;
CoreUtils.PROMISE_DEFAULT_TIMEOUT = 15000;

class CXOneConstants {
}
CXOneConstants.CXONE_TOKEN_HEADER = 'CXONETokenNeeded';
CXOneConstants.EVENTS = {
    TOKEN_EXPIRED: 'TOKEN_EXPIRED',
    USER_TOKEN_REFRESHED: 'USER_TOKEN_REFRESHED',
    GET_SOCK_JS_OBJECT: 'GET_SOCK_JS_OBJECT',
    CREATE_NEW_SOCK_JS_OBJECT: 'CREATE_NEW_SOCK_JS_OBJECT',
    ON_PAGE_VISIBLE: 'ON_PAGE_VISIBLE'
};
CXOneConstants.HTTP_HEADERS = {
    'retry-after': 'retry-after'
};
CXOneConstants.USER_TOKEN_LS_NAME = 'wfo_saas.userToken';
CXOneConstants.modulesConfigurationMapToFilterModules = {
    qma: {
        featureIds: [107, 103],
        applicationId: 'QM',
        removeModuleWithName: ['qm', 'qmp', 'qmp_wo_qma'],
        defaultModuleForLicense: 'false'
    },
    qm: {
        featureIds: [103],
        applicationId: 'QM',
        removeModuleWithName: ['qma', 'qmp', 'qmp_wo_qma'],
        defaultModuleForLicense: 'true'
    },
    qmp_wo_qma: {
        featureIds: [30100, 103],
        applicationId: 'QM',
        removeModuleWithName: ['qm', 'qma', 'qmp'],
        defaultModuleForLicense: 'false'
    },
    qmp: {
        featureIds: [30100, 103, 107],
        applicationId: 'QM',
        removeModuleWithName: ['qm', 'qma', 'qmp_wo_qma'],
        defaultModuleForLicense: 'false'
    }
};

class Weekdays {
    static all() {
        return CoreUtils.deepCopy(Weekdays.WEEKDAYS);
    }
    static byId(idSearch) {
        return CoreUtils.deepCopy(Weekdays.WEEKDAYS.find(({ id }) => id === idSearch));
    }
    static byShortKey(shortKeySearch) {
        return CoreUtils.deepCopy(Weekdays.WEEKDAYS.find(({ shortKey }) => shortKey === shortKeySearch));
    }
    static byLongKey(longKeySearch) {
        return CoreUtils.deepCopy(Weekdays.WEEKDAYS.find(({ longKey }) => longKey === longKeySearch));
    }
    static fromDate(date) {
        return Weekdays.byId(date.getDay());
    }
}
Weekdays.WEEKDAYS = [
    { id: 0, shortKey: 'SUN', longKey: 'SUNDAY', translationKey: 'weekDays.sunday' },
    { id: 1, shortKey: 'MON', longKey: 'MONDAY', translationKey: 'weekDays.monday' },
    { id: 2, shortKey: 'TUE', longKey: 'TUESDAY', translationKey: 'weekDays.tuesday' },
    { id: 3, shortKey: 'WED', longKey: 'WEDNESDAY', translationKey: 'weekDays.wednesday' },
    { id: 4, shortKey: 'THU', longKey: 'THURSDAY', translationKey: 'weekDays.thursday' },
    { id: 5, shortKey: 'FRI', longKey: 'FRIDAY', translationKey: 'weekDays.friday' },
    { id: 6, shortKey: 'SAT', longKey: 'SATURDAY', translationKey: 'weekDays.saturday' }
];

class Deferred {
    constructor() {
        this.state = 'pending';
        this.promise = new Promise((resolve, reject) => {
            this._resolve = resolve;
            this._reject = reject;
        });
    }
    resolve(value) {
        if (this.state === 'resolved') {
            return;
        }
        this.state = 'resolved';
        this._resolve(value);
    }
    reject(reason) {
        if (this.state === 'rejected') {
            return;
        }
        this.state = 'rejected';
        this._reject(reason);
    }
    isResolved() {
        return this.state === 'resolved';
    }
    isPending() {
        return this.state === 'pending';
    }
    isRejected() {
        return this.state === 'rejected';
    }
}

class DomUtils {
    static isTextEmpty(id) {
        const element = document.querySelector(`#${id}`);
        let value;
        if (element) {
            value = element.getAttribute('value');
            return !(value !== undefined && value !== '');
        }
        return false;
    }
    /**
     * Prevents vertical scrolling outside of the boundaries of the event target element.
     *
     * In other words, if you are using a mouse scroll wheel to scroll in an element that has a scroll bar,
     * and you get to the top or bottom, you won't accidentally start scrolling the entire page.
     */
    static captureVerticalScrollEvent(event) {
        if (!event.currentTarget) {
            return;
        }
        const scrollTop = event.currentTarget.scrollTop;
        const scrollHeight = event.currentTarget.scrollHeight;
        const clientHeight = event.currentTarget.clientHeight;
        const scrollingAtTop = scrollTop === 0 && event.deltaY > 0;
        const scrollingAtBottom = scrollTop + clientHeight >= scrollHeight && event.deltaY < 0;
        if (scrollingAtTop || scrollingAtBottom) {
            event.stopPropagation();
            event.preventDefault();
        }
    }
    /**
     * If user browser is Ie return true
     */
    static isIE(userAgent) {
        const ua = userAgent || window.navigator.userAgent;
        const msie = ua.indexOf('MSIE ');
        if (msie > 0) {
            // IE 10 or older => return version number
            return parseInt(ua.substring(msie + 5, ua.indexOf('.', msie)), 10);
        }
        const trident = ua.indexOf('Trident/');
        if (trident > 0) {
            // IE 11 => return version number
            const rv = ua.indexOf('rv:');
            return parseInt(ua.substring(rv + 3, ua.indexOf('.', rv)), 10);
        }
        const edge = ua.indexOf('Edge/');
        if (edge > 0) {
            // Edge (IE 12+) => return version number
            return parseInt(ua.substring(edge + 5, ua.indexOf('.', edge)), 10);
        }
        // other browser
        return false;
    }
    static addWfmScrollbarToElement(element, scrollbarOptions) {
        element.setAttribute('wfm-scrollbar', '');
        element.setAttribute('scrollbar-options', JSON.parse(scrollbarOptions));
    }
}

class ScreenUtils {
    static get instance() {
        if (!this._instance) {
            this._instance = new ScreenUtils();
        }
        return this._instance;
    }
    constructor() {
        this.defaultLayoutSetting = {
            isCustomLayout: false,
            isNiceHeaderHidden: false,
            isSidebarHidden: false,
            isToggleButtonEnabled: true
        };
        this.customLayoutSetting = {
            isCustomLayout: true,
            isNiceHeaderHidden: true,
            isSidebarHidden: false,
            isSidebarOpened: false,
            isToggleButtonEnabled: false
        };
        this.customLayoutValue = null;
        this.EMBEDDED_CLIENT_URL_SUFFIX = '?embeddedClient=true';
    }
    reset() {
        this.customLayoutValue = null;
    }
    getCustomLayoutValue() {
        return this.customLayoutValue;
    }
    setCustomLayoutValue(customLayoutVal) {
        this.customLayoutValue = customLayoutVal;
    }
    getLayoutSetting() {
        if (this.customLayoutValue) {
            return this.customLayoutSetting;
        }
        return this.defaultLayoutSetting;
    }
    getLinkSuffix() {
        if (this.customLayoutValue) {
            return this.EMBEDDED_CLIENT_URL_SUFFIX;
        }
        return '';
    }
    getWindowObject() {
        return window;
    }
    getScreenLayout(layouts) {
        let newLayout = null;
        const resolution = this.getWindowObject().innerWidth;
        if (resolution !== 0) {
            Object.keys(layouts).forEach((layoutKey) => {
                const layoutInfo = layouts[layoutKey];
                if (layoutInfo.minResolution < resolution && resolution <= layoutInfo.maxResolution) {
                    newLayout = {
                        screenLayout: layoutInfo.screenLayout,
                        minResolution: layoutInfo.minResolution,
                        maxResolution: layoutInfo.maxResolution,
                        currentScreenResolution: resolution
                    };
                }
            });
        }
        // set default(MAX) layout when screen width is 0 - happens when MAX initializes a CXOne page before it is opened.
        else {
            newLayout = {
                screenLayout: 2,
                minResolution: 370,
                maxResolution: 600,
                currentScreenResolution: 600
            };
        }
        return newLayout;
    }
}

class SubscribeService {
    constructor() {
        this.subscribeEvents = {};
        this.eventCounter = 0;
    }
    static get instance() {
        if (!this._instance) {
            this._instance = new SubscribeService();
        }
        return this._instance;
    }
    reset() {
        this.subscribeEvents = {};
        this.eventCounter = 0;
    }
    subscribeToEvent(eventName, callback) {
        if (!(eventName && callback && typeof (callback) === typeof (Function))) {
            return false;
        }
        SubscribeService._instance.eventCounter += 1;
        const eventID = SubscribeService._instance.eventCounter;
        if (!SubscribeService._instance.subscribeEvents[eventName]) {
            SubscribeService._instance.subscribeEvents[eventName] = {};
        }
        SubscribeService._instance.subscribeEvents[eventName][eventID] = callback;
        return () => {
            delete SubscribeService._instance.subscribeEvents[eventName][eventID];
        };
    }
    resetEventCallbacksByEventName(eventName) {
        SubscribeService._instance.subscribeEvents[eventName] = {};
    }
    resetAllEventCallbacks() {
        SubscribeService._instance.subscribeEvents = {};
    }
    getSubscribeCallbacks(eventName) {
        const results = [];
        if (!SubscribeService._instance.subscribeEvents[eventName]) {
            return [];
        }
        for (const key in SubscribeService._instance.subscribeEvents[eventName]) {
            if (SubscribeService._instance.subscribeEvents[eventName].hasOwnProperty(key)) {
                results.push(SubscribeService._instance.subscribeEvents[eventName][key]);
            }
        }
        return results;
    }
    invokeSubscribeCallbacks(eventName, data, isAsync) {
        const promises = [];
        for (const key in SubscribeService._instance.subscribeEvents[eventName]) {
            if (SubscribeService._instance.subscribeEvents[eventName].hasOwnProperty(key)) {
                const callback = SubscribeService._instance.subscribeEvents[eventName][key];
                if (isAsync) {
                    promises.push(callback(data));
                }
                else {
                    try {
                        callback(data);
                    }
                    catch (e) {
                        console.error('SUBSCRIPTION EVENT ERROR: ' + eventName + ': ' + e.message);
                    }
                }
            }
        }
        if (isAsync) {
            return Promise.all(promises);
        }
        return;
    }
}

class ScriptLoaderService {
    static loadResource(ele) {
        return new Promise((resolve) => {
            ele.onload = () => {
                resolve({ success: true });
            };
            ele.onerror = () => {
                resolve({ success: false });
            };
        });
    }
    static getScript(url) {
        return __awaiter(this, void 0, void 0, function* () {
            const bodyElement = document.querySelector('body');
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.src = url;
            const result = yield ScriptLoaderService.loadResource(script);
            if (!bodyElement || !result.success) {
                return { success: false };
            }
            bodyElement.appendChild(script);
            return result;
        });
    }
    static getCSS(url) {
        return __awaiter(this, void 0, void 0, function* () {
            const link = document.createElement('link');
            const headElement = document.querySelector('head');
            link.type = 'text/css';
            link.rel = 'stylesheet';
            link.href = url;
            const result = yield ScriptLoaderService.loadResource(link);
            if (!headElement || !result.success) {
                return { success: false };
            }
            headElement.appendChild(link);
            return result;
        });
    }
    static loadJsAndCss(urls) {
        return __awaiter(this, void 0, void 0, function* () {
            const promises = [];
            urls.map(url => {
                if (url.substring(url.lastIndexOf('.') + 1).indexOf('css') > -1) {
                    promises.push(this.getCSS(url));
                }
                else {
                    promises.push(this.getScript(url));
                }
                return url;
            });
            return Promise.all(promises);
        });
    }
}

const TimeZones = [
    {
        name: 'Midway Island, Samoa',
        ianaName: 'Pacific/Samoa'
    },
    {
        name: 'Hawaii',
        ianaName: 'US/Hawaii'
    },
    {
        name: 'Alaska',
        ianaName: 'America/Anchorage'
    },
    {
        name: 'Pacific Time (US & Canada)',
        ianaName: 'America/Los_Angeles'
    },
    {
        name: 'Tijuana, Baja California',
        ianaName: 'America/Tijuana'
    },
    {
        name: 'Arizona',
        ianaName: 'US/Arizona'
    },
    {
        name: 'Chihuahua, La Paz, Mazatlan',
        ianaName: 'America/Chihuahua'
    },
    {
        name: 'Mountain Time (US & Canada)',
        ianaName: 'America/Denver'
    },
    {
        name: 'Central Time (US & Canada)',
        ianaName: 'America/Chicago'
    },
    {
        name: 'Guadalajara, Mexico City, Monterrey',
        ianaName: 'America/Mexico_City'
    },
    {
        name: 'Saskatchewan',
        ianaName: 'Canada/Saskatchewan'
    },
    {
        name: 'Bogota, Lima, Quito, Rio Branco',
        ianaName: 'America/Bogota'
    },
    {
        name: 'Eastern Time (US & Canada)',
        ianaName: 'America/New_York'
    },
    {
        name: 'Indiana (East)',
        ianaName: 'America/Indiana/Indianapolis'
    },
    {
        name: 'Atlantic Time (Canada)',
        ianaName: 'America/Moncton'
    },
    {
        name: 'Caracas, La Paz',
        ianaName: 'America/Caracas'
    },
    {
        name: 'Manaus',
        ianaName: 'America/Manaus'
    },
    {
        name: 'Santiago',
        ianaName: 'America/Santiago'
    },
    {
        name: 'Newfoundland',
        ianaName: 'America/St_Johns'
    },
    {
        name: 'Brasilia',
        ianaName: 'America/Fortaleza'
    },
    {
        name: 'Buenos Aires, Georgetown',
        ianaName: 'America/Argentina/Buenos_Aires'
    },
    {
        name: 'Montevideo',
        ianaName: 'America/Montevideo'
    },
    {
        name: 'Azores',
        ianaName: 'Atlantic/Azores'
    },
    {
        name: 'Casablanca, Monrovia, Reykjavik',
        ianaName: 'Africa/Casablanca'
    },
    {
        name: 'Greenwich Mean Time',
        ianaName: 'Etc/GMT'
    },
    {
        name: 'Greenwich Mean Time : Dublin, Edinburgh, Lisbon, London',
        ianaName: 'Europe/London'
    },
    {
        name: 'Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna',
        ianaName: 'Europe/Amsterdam'
    },
    {
        name: 'Belgrade, Bratislava, Budapest, Ljubljana, Prague',
        ianaName: 'Europe/Belgrade'
    },
    {
        name: 'Brussels, Copenhagen, Madrid, Paris',
        ianaName: 'Europe/Brussels'
    },
    {
        name: 'Sarajevo, Skopje, Warsaw, Zagreb',
        ianaName: 'Europe/Sarajevo'
    },
    {
        name: 'Amman',
        ianaName: 'Asia/Amman'
    },
    {
        name: 'Athens, Bucharest, Istanbul',
        ianaName: 'Europe/Athens'
    },
    {
        name: 'Beirut',
        ianaName: 'Asia/Beirut'
    },
    {
        name: 'Cairo',
        ianaName: 'Africa/Cairo'
    },
    {
        name: 'Harare, Pretoria',
        ianaName: 'Africa/Harare'
    },
    {
        name: 'Helsinki, Kyiv, Riga, Sofia, Tallinn, Vilnius',
        ianaName: 'Europe/Helsinki'
    },
    {
        name: 'Jerusalem',
        ianaName: 'Asia/Jerusalem'
    },
    {
        name: 'Minsk',
        ianaName: 'Europe/Minsk'
    },
    {
        name: 'Windhoek',
        ianaName: 'Africa/Windhoek'
    },
    {
        name: 'Kuwait, Riyadh, Baghdad',
        ianaName: 'Asia/Kuwait'
    },
    {
        name: 'Moscow, St. Petersburg, Volgograd',
        ianaName: 'Europe/Moscow'
    },
    {
        name: 'Nairobi',
        ianaName: 'Africa/Nairobi'
    },
    {
        name: 'Tbilisi',
        ianaName: 'Asia/Tbilisi'
    },
    {
        name: 'Tehran',
        ianaName: 'Asia/Tehran'
    },
    {
        name: 'Abu Dhabi, Muscat',
        ianaName: 'Asia/Dubai'
    },
    {
        name: 'Baku',
        ianaName: 'Asia/Baku'
    },
    {
        name: 'Yerevan',
        ianaName: 'Asia/Yerevan'
    },
    {
        name: 'Kabul',
        ianaName: 'Asia/Kabul'
    },
    {
        name: 'Yekaterinburg',
        ianaName: 'Asia/Yekaterinburg'
    },
    {
        name: 'Islamabad, Karachi, Tashkent',
        ianaName: 'Asia/Karachi'
    },
    {
        name: ' Chennai, Kolkata, Mumbai, New Delhi',
        ianaName: 'Asia/Kolkata'
    },
    {
        name: 'Kathmandu',
        ianaName: 'Asia/Kathmandu'
    },
    {
        name: 'Almaty, Novosibirsk',
        ianaName: 'Asia/Almaty'
    },
    {
        name: ' Astana, Dhaka',
        ianaName: 'Asia/Dhaka'
    },
    {
        name: 'Yangon (Rangoon)',
        ianaName: 'Asia/Rangoon'
    },
    {
        name: ' Bangkok, Hanoi, Jakarta',
        ianaName: 'Asia/Bangkok'
    },
    {
        name: 'Krasnoyarsk',
        ianaName: 'Asia/Krasnoyarsk'
    },
    {
        name: 'Beijing, Chongqing, Hong Kong, Urumqi',
        ianaName: 'Asia/Hong_Kong'
    },
    {
        name: 'Kuala Lumpur, Singapore',
        ianaName: 'Asia/Kuala_Lumpur'
    },
    {
        name: 'Irkutsk, Ulaan Bataar',
        ianaName: 'Asia/Irkutsk'
    },
    {
        id: '67',
        name: 'Perth',
        ianaName: 'Australia/Perth'
    },
    {
        name: 'Taipei',
        ianaName: 'Asia/Taipei'
    },
    {
        name: 'Osaka, Sapporo, Tokyo',
        ianaName: 'Asia/Tokyo'
    },
    {
        name: 'Seoul',
        ianaName: 'Asia/Seoul'
    },
    {
        name: 'Yakutsk',
        ianaName: 'Asia/Yakutsk'
    },
    {
        name: ' Adelaide',
        ianaName: 'Australia/Adelaide'
    },
    {
        name: 'Darwin',
        ianaName: 'Australia/Darwin'
    },
    {
        name: 'Brisbane',
        ianaName: 'Australia/Brisbane'
    },
    {
        name: 'Canberra, Melbourne, Sydney',
        ianaName: 'Australia/Sydney'
    },
    {
        name: 'Hobart',
        ianaName: 'Australia/Hobart'
    },
    {
        name: 'Guam, Port Moresby',
        ianaName: 'Pacific/Guam'
    },
    {
        name: 'Vladivostok',
        ianaName: 'Asia/Vladivostok'
    },
    {
        name: 'Magadan, Solomon Is., New Caledonia',
        ianaName: 'Asia/Magadan'
    },
    {
        name: 'Auckland, Wellington',
        ianaName: 'Pacific/Auckland'
    },
    {
        name: 'Fiji, Kamchatka, Marshall Is.',
        ianaName: 'Pacific/Fiji'
    }
];

class TimeZoneUtils {
    constructor() { }
    static get instance() {
        if (!this._instance) {
            this._instance = new TimeZoneUtils();
        }
        return this._instance;
    }
    clearCache() {
        this.timeZoneCache = undefined;
    }
    getRawTimeZones() {
        return TimeZones;
    }
    getTimeZoneList(withoutUTCInName) {
        return new Promise((resolve) => {
            if (!this.timeZoneCache) {
                const timeZones = this.getRawTimeZones().map((val) => {
                    const momentTZ = moment(DateUtils.now()).tz(val.ianaName ? val.ianaName : '');
                    let name;
                    if (withoutUTCInName) {
                        name = momentTZ ? val.name : val.name + ' tz not found';
                    }
                    else {
                        name = momentTZ ? '(UTC' + momentTZ.format('Z') + ') ' + val.name : val.name + ' tz not found';
                    }
                    return {
                        name,
                        offset: momentTZ ? momentTZ.utcOffset() : 0,
                        value: val.ianaName,
                        ianaName: val.ianaName
                    };
                });
                this.timeZoneCache = timeZones;
            }
            resolve(CoreUtils.deepCopy(this.timeZoneCache));
        });
    }
    getMomentLocalOffset() {
        let offset = moment().format('ZZ').replace(/0/g, '');
        if (offset.includes('+')) {
            offset = offset.replace('+', '');
        }
        return offset;
    }
    getLocalTimeZone() {
        const offset = TimeZoneUtils.instance.getMomentLocalOffset();
        return new Promise((resolve) => {
            TimeZoneUtils.instance.getTimeZoneList().then((result) => {
                const currentTimezone = result.filter((item) => item.value === offset)[0];
                resolve(currentTimezone);
            });
        });
    }
    isUSTimezone() {
        const usTimezones = ['PST', 'PDT', 'MST', 'MDT', 'CST', 'CDT', 'EST', 'EDT', 'HST', 'HDT', 'AKST', 'AKDT'];
        return usTimezones.includes(moment.tz(moment.tz.guess()).format('z'));
    }
    isThereATimeChange(startRangeToCheck, endRangeToCheck, thruEndOfDay, tz) {
        let dateToCheck = moment(startRangeToCheck).startOf('day');
        let prevDate;
        let dateAsString;
        if (!tz) {
            tz = moment.tz.guess();
        }
        dateAsString = moment(startRangeToCheck).format('YYYY-MM-DD HH:mm');
        dateToCheck = moment.tz(dateAsString, tz);
        if (thruEndOfDay) {
            dateAsString = moment(endRangeToCheck).format('YYYY-MM-DD 00:00');
        }
        else {
            dateAsString = moment(endRangeToCheck).format('YYYY-MM-DD HH:mm');
        }
        endRangeToCheck = moment.tz(dateAsString, tz);
        if (thruEndOfDay) {
            endRangeToCheck.endOf('day');
        }
        while (DateUtils.isSameOrBefore(dateToCheck.toDate(), endRangeToCheck.toDate())) {
            if (prevDate) {
                if (moment.tz(prevDate, tz).isDST() !== moment.tz(dateToCheck, tz).isDST()) {
                    return !moment.tz(prevDate, tz).isDST() && moment.tz(dateToCheck, tz).isDST()
                        ? DateConstants.DST.SPRING_AHEAD_TIMECHANGE
                        : DateConstants.DST.FALL_BEHIND_TIMECHANGE;
                }
            }
            prevDate = dateToCheck.clone();
            dateToCheck.add(1, 'hour');
        }
        return DateConstants.DST.NO_TIMECHANGE;
    }
    getTimeZoneDifferenceInHours(zone1, zone2, date) {
        const dateAsString = moment(date).format('YYYY-MM-DD HH:mm');
        const zone1Offset = moment.tz(dateAsString, zone1).utcOffset();
        const zone2Offset = moment.tz(dateAsString, zone2).utcOffset();
        let diff = ((zone1Offset - zone2Offset) / 60) * -1;
        const tzChangedSource = TimeZoneUtils.instance.isThereATimeChange(date, date, true, zone1);
        const tzChangedDestination = TimeZoneUtils.instance.isThereATimeChange(date, date, true, zone2);
        // Was there a DST change in the source TZ and not in the destination TZ
        if (tzChangedSource !== DateConstants.DST.NO_TIMECHANGE && tzChangedDestination === DateConstants.DST.NO_TIMECHANGE) {
            if (tzChangedSource === DateConstants.DST.FALL_BEHIND_TIMECHANGE) {
                diff += 1;
            }
            else if (tzChangedSource === DateConstants.DST.SPRING_AHEAD_TIMECHANGE) {
                diff -= 1;
            }
        }
        return diff;
    }
    convertDateToNativeTimeZone(d, timeZone) {
        const newOffset = d.getTimezoneOffset();
        const dateAsString = moment(d).format('YYYY-MM-DD HH:mm');
        const originalOffset = moment.tz(dateAsString, timeZone).utcOffset();
        if (originalOffset * -1 === newOffset) {
            return d;
        }
        const utc = d.getTime() - (originalOffset + newOffset) * 60000;
        return new Date(utc);
    }
    convertNativeDateToTimeZoneDate(d, timeZone) {
        const formattedDate = moment(new Date(d)).tz(timeZone).format('YYYY-MM-DD HH:mm:ss.SSS');
        return moment(formattedDate).toDate();
    }
    createMomentWithTZWithoutAdjustingTime(dateObject, tz) {
        return moment.tz(moment(dateObject).format('YYYY-MM-DD HH:mm:ss.SSS'), tz);
    }
}

class ConfigurationService {
    constructor() {
        this.DEFAULT_CONFIG = {
            [ConfigurationService.COMMON_KEYS.ApiServerBaseUrl]: '',
            [ConfigurationService.COMMON_KEYS.GlobalWebappUrl]: '',
            [ConfigurationService.COMMON_KEYS.WebsocketServerBaseUrl]: ''
        };
        this.reset();
    }
    static get instance() {
        if (!this._instance) {
            this._instance = new ConfigurationService();
        }
        return this._instance;
    }
    reset() {
        this.configuration = Object.assign({}, this.DEFAULT_CONFIG);
    }
    setConfiguration(configuration) {
        this.configuration = Object.assign(Object.assign({}, this.DEFAULT_CONFIG), configuration);
    }
    all() {
        return this.configuration;
    }
    get(key) {
        return this.configuration[key];
    }
    set(key, value) {
        this.configuration[key] = value;
    }
}
ConfigurationService.COMMON_KEYS = {
    AreaIdentifier: 'areaIdentifier',
    ApiFacadeEndpoint: 'apiFacadeEndpoint',
    ApiFacadeOptOut: 'apiFacadeOptOut',
    ApiServerBaseUrl: 'apiServerBaseUrl',
    CurrentEnv: 'currentEnv',
    GlobalWebappUrl: 'global.web_app.url',
    StaticBaseUrl: 'staticBaseUrl',
    WebsocketServerBaseUrl: 'websocketServerBaseUrl'
};

class WindowService {
    static get instance() {
        if (!this._instance) {
            this._instance = new WindowService();
        }
        return this._instance;
    }
    constructor() {
        this.key = 'evolveChildWindows';
        const host = this.getWindowObject().location.host;
        this.domain = host.indexOf(':') > 0 ?
            host.substring(host.indexOf('.'), host.indexOf(':')) :
            host.substring(host.indexOf('.'));
    }
    getWindowObject() {
        return window;
    }
    getSuffix() {
        const timeStamp = new Date();
        return timeStamp.getTime() * Math.floor((Math.random() * 10) + 1);
    }
    setCookie(values) {
        CookieUtils.setCookie(this.key, JSON.stringify(values), { domain: this.domain, path: '/' });
    }
    openWindow(url, name, params, dontSetEvolveCookie = false) {
        if (!url) {
            return;
        }
        name = name ? name + this.getSuffix() : 'evolveWindow' + this.getSuffix();
        const cookie = CookieUtils.getCookie(this.key);
        const values = cookie ? JSON.parse(cookie) : [];
        values.push(name);
        if (!dontSetEvolveCookie) {
            this.setCookie(values);
        }
        return this.getWindowObject().open(url, name, params);
    }
    closeWindow() {
        const cookie = CookieUtils.getCookie(this.key);
        if (cookie) {
            const values = JSON.parse(cookie);
            values.forEach(value => {
                const window = this.getWindowObject().open('', value);
                if (window) {
                    window.close();
                }
            });
        }
        CookieUtils.removeCookie(this.key, { domain: this.domain, path: '/' });
    }
}

// URL Sanitization base on sanitize-url: https://github.com/braintree/sanitize-url/tree/main
class HttpUrlUtils {
    static get instance() {
        if (!this._instance) {
            this._instance = new HttpUrlUtils();
        }
        return this._instance;
    }
    constructor() {
        this.invalidProtocolRegex = /^([^\w]*)(javascript|data|vbscript|<marquee[^>]*onstart)/im; // match: any character zero or more times, followed by javascript, data, or vbscript
        this.invalidXssEncodedRegex = /[\s\0]*((\%3C)|<)[^\n]+((\%3E)|>)[\s\0]*/gi; // match: < or %3C, followed by any character zero or more times, followed by > or %3E
        // match: <script|<img|<body|"><script|javascript:alert|<iframe|<marquee|<svg|<input|<div
        this.invalidXSSRegex = /\?redirect=javascript:alert|(?:<script[^>]*>|<img[^>]*onerror|<body[^>]*onload|"><script[^>]*>|javascript:alert|<iframe[^>]*src=javascript|<marquee[^>]*onstart|<svg[^>]*onload|<input[^>]*onfocus|<div[^>]*onmouseover)[^>]*>/gi;
        this.htmlEntitiesRegex = /&#(\w+)(^\w|;)?/g; // match: &#|;word characters (letters, digits, or underscores)
        this.htmlCtrlEntityRegex = /&(newline|tab);/gi; // match: &newline; or &tab;
        /*eslint no-control-regex: "warn"*/
        this.unicodeCharactersRegex = /[\u0000-\u001F\u007F-\u009F\u2000-\u200D\uFEFF]/gim; // match: unicode control characters
        this.urlSchemeRegex = /^.+(:|&colon;)/gim; // match: any character one or more times, followed by a colon
        this.relativeFirstCharacters = ['.', '/'];
        this.emptyUrl = '';
    }
    isRelativeUrlWithoutProtocol(url) {
        return this.relativeFirstCharacters.indexOf(url[0]) > -1;
    }
    decodeHtmlCharacters(str) {
        return str.replace(this.htmlEntitiesRegex, (_match, dec) => {
            return String.fromCharCode(dec);
        });
    }
    sanitizeUrlXss(url) {
        const sanitizedUrl = this.decodeHtmlCharacters(url || '')
            .replace(this.htmlCtrlEntityRegex, '')
            .replace(this.unicodeCharactersRegex, '').trim();
        if (!sanitizedUrl || this.invalidXssEncodedRegex.test(sanitizedUrl) || this.invalidXSSRegex.test(sanitizedUrl)) {
            console.error('Invalid URL:', sanitizedUrl);
            return this.emptyUrl;
        }
        if (this.isRelativeUrlWithoutProtocol(sanitizedUrl)) {
            return sanitizedUrl;
        }
        const urlSchemeParseResults = sanitizedUrl.match(this.urlSchemeRegex);
        if (!urlSchemeParseResults) {
            return sanitizedUrl;
        }
        const urlScheme = urlSchemeParseResults[0];
        if (this.invalidProtocolRegex.test(urlScheme)) {
            console.error('Invalid URL Schema:', urlScheme);
            return this.emptyUrl;
        }
        // Return the sanitized URL string
        return sanitizedUrl;
    }
    sanitizeUrl(urlString) {
        try {
            const url = new URL(urlString);
            // Iterate over each query parameter and encode its value using encodeURIComponent()
            url.searchParams.forEach((value, key) => {
                const encodedValue = encodeURIComponent(value);
                url.searchParams.set(key, encodedValue);
            });
            // Return the sanitized URI as a string
            return url.href;
        }
        catch (error) {
            console.error('Invalid URI:', error);
            return this.emptyUrl;
        }
    }
}

class URLUtils {
    static getWindowObject() {
        return window;
    }
    static redirectToUrl(href) {
        URLUtils.getWindowObject().location.href = HttpUrlUtils.instance.sanitizeUrlXss(href);
    }
    static getLocationPathname() {
        return URLUtils.getWindowObject().location.pathname;
    }
    static getAppContextFromUrl() {
        return URLUtils.getLocationPathname().split('/')[1];
    }
    static getLocationHash() {
        return URLUtils.getWindowObject().location.hash;
    }
    static getPageUrl() {
        const hashValue = URLUtils.getWindowObject().location.hash;
        let pageUrl = '';
        if (hashValue) {
            pageUrl = hashValue;
            if (hashValue.startsWith('#')) {
                pageUrl = pageUrl.substring(1);
            }
        }
        else {
            pageUrl = '/' + URLUtils.getWindowObject().location.pathname.split('/')[2];
        }
        return pageUrl;
    }
    static getQueryParameter(field, href) {
        const reg = new RegExp('[?&]' + field + '=([^&#]*)', 'i');
        const str = reg.exec(href);
        return str ? str[1] : null;
    }
    static hasParameter(url, paramName) {
        const value = URLUtils.getQueryParameter(paramName, url);
        if (value && value.length > 0) {
            return true;
        }
        return false;
    }
    static queryStringUrlReplacement(param, value) {
        const url = URLUtils.getWindowObject().location.href;
        const re = new RegExp('[\\?&]' + param + '=([^&#]*)');
        const match = re.exec(url);
        let delimiter;
        let newString;
        if (match === null) {
            // append new param
            const hasQuestionMark = /\?/.test(url);
            delimiter = hasQuestionMark ? '&' : '?';
            newString = url + delimiter + param + '=' + value;
        }
        else {
            delimiter = match[0].charAt(0);
            newString = url.replace(re, delimiter + param + '=' + value);
        }
        return newString;
    }
    static getAuthHost() {
        const protocol = URLUtils.getWindowObject().location.protocol;
        let host = URLUtils.getWindowObject().location.hostname;
        const port = URLUtils.getWindowObject().location.port;
        const hostparts = host.split('.');
        if (hostparts.length !== 0) {
            const sub = hostparts[0];
            host = host.replace(sub, 'auth');
        }
        else {
            host = host.replace('na1', 'auth');
        }
        return protocol + '//' + host + (port ? ':' + port : '');
    }
    static getAppHost() {
        const protocol = URLUtils.getWindowObject().location.protocol;
        let host = URLUtils.getWindowObject().location.hostname;
        const port = URLUtils.getWindowObject().location.port;
        const index = host.indexOf('.');
        if (host.substring(0, index) !== 'auth' && host.substring(0, index) !== 'na1') {
            host = host.substring(index + 1);
        }
        host = host.replace('auth', 'na1');
        return protocol + '//' + host + ':' + (port || '');
    }
    static encodeUriQuery(val, pctEncodeSpaces) {
        return encodeURIComponent(val).
            replace(/%40/gi, '@').
            replace(/%3A/gi, ':').
            replace(/%24/g, '$').
            replace(/%2C/gi, ',').
            replace(/%3B/gi, ';').
            replace(/%20/g, (pctEncodeSpaces ? '%20' : '+'));
    }
    static jQueryLikeParamSerializer(params) {
        if (!params) {
            return '';
        }
        const parts = [];
        const serialize = (toSerialize, prefix, topLevel) => {
            if (ObjectUtils.isArray(toSerialize)) {
                toSerialize.forEach((value, index) => {
                    serialize(value, prefix + '[' + (ObjectUtils.isObject(value) ? index : '') + ']');
                });
            }
            else if (ObjectUtils.isObject(toSerialize) && !ObjectUtils.isDate(toSerialize)) {
                ObjectUtils.forEachSorted(toSerialize, (value, key) => {
                    serialize(value, prefix +
                        (topLevel ? '' : '[') +
                        key +
                        (topLevel ? '' : ']'));
                });
            }
            else {
                if (ObjectUtils.isFunction(toSerialize)) {
                    toSerialize = toSerialize();
                }
                parts.push(URLUtils.encodeUriQuery(prefix) + '=' +
                    (toSerialize == null ? '' :
                        URLUtils.encodeUriQuery(ObjectUtils.serializeValue(toSerialize))));
            }
        };
        serialize(params, '', true);
        return parts.join('&');
    }
    static getCxoneHost() {
        const protocol = URLUtils.getWindowObject().location.protocol;
        const host = URLUtils.getWindowObject().location.hostname;
        const port = URLUtils.getWindowObject().location.port;
        let domain;
        const env = ConfigurationService.instance.get('currentEnv');
        if (host.indexOf('localhost') > -1) {
            domain = '.dev.localhost';
        }
        else {
            domain = '.' + (env !== undefined && env !== '' ? (env + '.') : '') + 'niceincontact.com';
        }
        return protocol + '://' + 'cxone' + domain + (port ? ':' + port : '');
    }
    static isUriPathExist(path) {
        return URLUtils.getWindowObject().location.href.indexOf(path) !== -1;
    }
    static removeURLParameter(url, paramName) {
        if (url.indexOf('?') < 0) {
            return url;
        }
        const leftUrl = url.substring(0, url.indexOf('?'));
        let rightUrl = url.substring(url.indexOf('?'));
        const searchOptions = new URLSearchParams(rightUrl);
        searchOptions.delete(paramName);
        const searchOptionsString = searchOptions.toString();
        if (searchOptionsString.length > 0) {
            rightUrl = '?' + searchOptionsString;
        }
        else {
            rightUrl = '';
        }
        return leftUrl + rightUrl;
    }
    static isExcludedApp(url, ignoreAppList) {
        const parser = document.createElement('a');
        parser.href = HttpUrlUtils.instance.sanitizeUrl(url);
        const pathSplit = parser.href.split('/');
        const appContext = pathSplit[3];
        ignoreAppList = ignoreAppList || [];
        const appExcludedUrl = ignoreAppList.find(item => {
            return appContext.indexOf(item) > -1;
        });
        return appExcludedUrl && appExcludedUrl.length > 0 ? true : false;
    }
    static getDomain() {
        return WindowService.instance.getWindowObject().location.host.split('.')[0];
    }
    static addURLQueryParam(url, queryParam) {
        if (url.indexOf('?') > -1) {
            url = url + '&' + queryParam + '=';
        }
        else {
            url = url + '?' + queryParam + '=';
        }
        return url;
    }
    static redirectToMfe(newPathName, includePage) {
        const pathName = newPathName ? newPathName : URLUtils.getLocationPathname().slice(1, -1);
        const page = includePage ? URLUtils.getLocationHash().substring(2) : '';
        URLUtils.redirectToUrl(`/apps/#/${pathName}/${page}`);
    }
    static isUrlValid(path) {
        try {
            new URL(path);
            return true;
        }
        catch (err) {
            return false;
        }
    }
}

const Countries = [
    {
        countryId: 1,
        code: 'AF',
        name: 'Afghanistan',
        value: 'Afghanistan'
    },
    {
        countryId: 2,
        code: 'AX',
        name: 'Åland Islands',
        value: 'Åland Islands'
    },
    {
        countryId: 3,
        code: 'AL',
        name: 'Albania',
        value: 'Albania'
    },
    {
        countryId: 4,
        code: 'DZ',
        name: 'Algeria',
        value: 'Algeria'
    },
    {
        countryId: 5,
        code: 'AS',
        name: 'American Samoa',
        value: 'American Samoa'
    },
    {
        countryId: 6,
        code: 'AD',
        name: 'Andorra',
        value: 'Andorra'
    },
    {
        countryId: 7,
        code: 'AO',
        name: 'Angola',
        value: 'Angola'
    },
    {
        countryId: 8,
        code: 'AI',
        name: 'Anguilla',
        value: 'Anguilla'
    },
    {
        countryId: 9,
        code: 'AQ',
        name: 'Antarctica',
        value: 'Antarctica'
    },
    {
        countryId: 10,
        code: 'AG',
        name: 'Antigua and Barbuda',
        value: 'Antigua and Barbuda'
    },
    {
        countryId: 11,
        code: 'AR',
        name: 'Argentina',
        value: 'Argentina'
    },
    {
        countryId: 12,
        code: 'AM',
        name: 'Armenia',
        value: 'Armenia'
    },
    {
        countryId: 13,
        code: 'AW',
        name: 'Aruba',
        value: 'Aruba'
    },
    {
        countryId: 14,
        code: 'AU',
        name: 'Australia',
        value: 'Australia'
    },
    {
        countryId: 15,
        code: 'AT',
        name: 'Austria',
        value: 'Austria'
    },
    {
        countryId: 16,
        code: 'AZ',
        name: 'Azerbaijan',
        value: 'Azerbaijan'
    },
    {
        countryId: 17,
        code: 'BS',
        name: 'Bahamas',
        value: 'Bahamas'
    },
    {
        countryId: 18,
        code: 'BH',
        name: 'Bahrain',
        value: 'Bahrain'
    },
    {
        countryId: 19,
        code: 'BD',
        name: 'Bangladesh',
        value: 'Bangladesh'
    },
    {
        countryId: 20,
        code: 'BB',
        name: 'Barbados',
        value: 'Barbados'
    },
    {
        countryId: 21,
        code: 'BY',
        name: 'Belarus',
        value: 'Belarus'
    },
    {
        countryId: 22,
        code: 'BE',
        name: 'Belgium',
        value: 'Belgium'
    },
    {
        countryId: 23,
        code: 'BZ',
        name: 'Belize',
        value: 'Belize'
    },
    {
        countryId: 24,
        code: 'BJ',
        name: 'Benin',
        value: 'Benin'
    },
    {
        countryId: 25,
        code: 'BM',
        name: 'Bermuda',
        value: 'Bermuda'
    },
    {
        countryId: 26,
        code: 'BT',
        name: 'Bhutan',
        value: 'Bhutan'
    },
    {
        countryId: 27,
        code: 'BO',
        name: 'Bolivia, Plurinational state of',
        value: 'Bolivia, Plurinational state of'
    },
    {
        countryId: 28,
        code: 'BQ',
        name: 'Bonaire, Sint Eustatius and Saba',
        value: 'Bonaire, Sint Eustatius and Saba'
    },
    {
        countryId: 29,
        code: 'BA',
        name: 'Bosnia and Herzegovina',
        value: 'Bosnia and Herzegovina'
    },
    {
        countryId: 30,
        code: 'BW',
        name: 'Botswana',
        value: 'Botswana'
    },
    {
        countryId: 31,
        code: 'BV',
        name: 'Bouvet Island',
        value: 'Bouvet Island'
    },
    {
        countryId: 32,
        code: 'BR',
        name: 'Brazil',
        value: 'Brazil'
    },
    {
        countryId: 33,
        code: 'IO',
        name: 'British Indian Ocean Territory',
        value: 'British Indian Ocean Territory'
    },
    {
        countryId: 34,
        code: 'BN',
        name: 'Brunei Darussalam',
        value: 'Brunei Darussalam'
    },
    {
        countryId: 35,
        code: 'BG',
        name: 'Bulgaria',
        value: 'Bulgaria'
    },
    {
        countryId: 36,
        code: 'BF',
        name: 'Burkina Faso',
        value: 'Burkina Faso'
    },
    {
        countryId: 37,
        code: 'BI',
        name: 'Burundi',
        value: 'Burundi'
    },
    {
        countryId: 38,
        code: 'KH',
        name: 'Cambodia',
        value: 'Cambodia'
    },
    {
        countryId: 39,
        code: 'CM',
        name: 'Cameroon',
        value: 'Cameroon'
    },
    {
        countryId: 40,
        code: 'CA',
        name: 'Canada',
        value: 'Canada'
    },
    {
        countryId: 41,
        code: 'CV',
        name: 'Cape Verde',
        value: 'Cape Verde'
    },
    {
        countryId: 42,
        code: 'KY',
        name: 'Cayman Islands',
        value: 'Cayman Islands'
    },
    {
        countryId: 43,
        code: 'CF',
        name: 'Central African Republic',
        value: 'Central African Republic'
    },
    {
        countryId: 44,
        code: 'TD',
        name: 'Chad',
        value: 'Chad'
    },
    {
        countryId: 45,
        code: 'CL',
        name: 'Chile',
        value: 'Chile'
    },
    {
        countryId: 46,
        code: 'CN',
        name: 'China',
        value: 'China'
    },
    {
        countryId: 47,
        code: 'CX',
        name: 'Christmas Island',
        value: 'Christmas Island'
    },
    {
        countryId: 48,
        code: 'CC',
        name: 'Cocos (Keeling) Islands',
        value: 'Cocos (Keeling) Islands'
    },
    {
        countryId: 49,
        code: 'CO',
        name: 'Colombia',
        value: 'Colombia'
    },
    {
        countryId: 50,
        code: 'KM',
        name: 'Comoros',
        value: 'Comoros'
    },
    {
        countryId: 51,
        code: 'CG',
        name: 'Congo',
        value: 'Congo'
    },
    {
        countryId: 52,
        code: 'CD',
        name: 'Congo, The Democratic Republic of The',
        value: 'Congo, The Democratic Republic of The'
    },
    {
        countryId: 53,
        code: 'CK',
        name: 'Cook Islands',
        value: 'Cook Islands'
    },
    {
        countryId: 54,
        code: 'CR',
        name: 'Costa Rica',
        value: 'Costa Rica'
    },
    {
        countryId: 55,
        code: 'CI',
        name: 'Côte D\'ivoire',
        value: 'Côte D\'ivoire'
    },
    {
        countryId: 56,
        code: 'HR',
        name: 'Croatia',
        value: 'Croatia'
    },
    {
        countryId: 57,
        code: 'CU',
        name: 'Cuba',
        value: 'Cuba'
    },
    {
        countryId: 58,
        code: 'CW',
        name: 'Curaçao',
        value: 'Curaçao'
    },
    {
        countryId: 59,
        code: 'CY',
        name: 'Cyprus',
        value: 'Cyprus'
    },
    {
        countryId: 60,
        code: 'CZ',
        name: 'Czech Republic',
        value: 'Czech Republic'
    },
    {
        countryId: 61,
        code: 'DK',
        name: 'Denmark',
        value: 'Denmark'
    },
    {
        countryId: 62,
        code: 'DJ',
        name: 'Djibouti',
        value: 'Djibouti'
    },
    {
        countryId: 63,
        code: 'DM',
        name: 'Dominica',
        value: 'Dominica'
    },
    {
        countryId: 64,
        code: 'DO',
        name: 'Dominican Republic',
        value: 'Dominican Republic'
    },
    {
        countryId: 65,
        code: 'EC',
        name: 'Ecuador',
        value: 'Ecuador'
    },
    {
        countryId: 66,
        code: 'EG',
        name: 'Egypt',
        value: 'Egypt'
    },
    {
        countryId: 67,
        code: 'SV',
        name: 'El Salvador',
        value: 'El Salvador'
    },
    {
        countryId: 68,
        code: 'GQ',
        name: 'Equatorial Guinea',
        value: 'Equatorial Guinea'
    },
    {
        countryId: 69,
        code: 'ER',
        name: 'Eritrea',
        value: 'Eritrea'
    },
    {
        countryId: 70,
        code: 'EE',
        name: 'Estonia',
        value: 'Estonia'
    },
    {
        countryId: 71,
        code: 'ET',
        name: 'Ethiopia',
        value: 'Ethiopia'
    },
    {
        countryId: 72,
        code: 'FK',
        name: 'Falkland Islands (Malvinas)',
        value: 'Falkland Islands (Malvinas)'
    },
    {
        countryId: 73,
        code: 'FO',
        name: 'Faroe Islands',
        value: 'Faroe Islands'
    },
    {
        countryId: 74,
        code: 'FJ',
        name: 'Fiji',
        value: 'Fiji'
    },
    {
        countryId: 75,
        code: 'FI',
        name: 'Finland',
        value: 'Finland'
    },
    {
        countryId: 76,
        code: 'FR',
        name: 'France',
        value: 'France'
    },
    {
        countryId: 77,
        code: 'GF',
        name: 'French Guiana',
        value: 'French Guiana'
    },
    {
        countryId: 78,
        code: 'PF',
        name: 'French Polynesia',
        value: 'French Polynesia'
    },
    {
        countryId: 79,
        code: 'TF',
        name: 'French Southern Territories',
        value: 'French Southern Territories'
    },
    {
        countryId: 80,
        code: 'GA',
        name: 'Gabon',
        value: 'Gabon'
    },
    {
        countryId: 81,
        code: 'GM',
        name: 'Gambia',
        value: 'Gambia'
    },
    {
        countryId: 82,
        code: 'GE',
        name: 'Georgia',
        value: 'Georgia'
    },
    {
        countryId: 83,
        code: 'DE',
        name: 'Germany',
        value: 'Germany'
    },
    {
        countryId: 84,
        code: 'GH',
        name: 'Ghana',
        value: 'Ghana'
    },
    {
        countryId: 85,
        code: 'GI',
        name: 'Gibraltar',
        value: 'Gibraltar'
    },
    {
        countryId: 86,
        code: 'GR',
        name: 'Greece',
        value: 'Greece'
    },
    {
        countryId: 87,
        code: 'GL',
        name: 'Greenland',
        value: 'Greenland'
    },
    {
        countryId: 88,
        code: 'GD',
        name: 'Grenada',
        value: 'Grenada'
    },
    {
        countryId: 89,
        code: 'GP',
        name: 'Guadeloupe',
        value: 'Guadeloupe'
    },
    {
        countryId: 90,
        code: 'GU',
        name: 'Guam',
        value: 'Guam'
    },
    {
        countryId: 91,
        code: 'GT',
        name: 'Guatemala',
        value: 'Guatemala'
    },
    {
        countryId: 92,
        code: 'GG',
        name: 'Guernsey',
        value: 'Guernsey'
    },
    {
        countryId: 93,
        code: 'GN',
        name: 'Guinea',
        value: 'Guinea'
    },
    {
        countryId: 94,
        code: 'GW',
        name: 'Guinea-Bissau',
        value: 'Guinea-Bissau'
    },
    {
        countryId: 95,
        code: 'GY',
        name: 'Guyana',
        value: 'Guyana'
    },
    {
        countryId: 96,
        code: 'HT',
        name: 'Haiti',
        value: 'Haiti'
    },
    {
        countryId: 97,
        code: 'HM',
        name: 'Heard Island and McDonald Islands',
        value: 'Heard Island and McDonald Islands'
    },
    {
        countryId: 98,
        code: 'VA',
        name: 'Holy See (vatican city state)',
        value: 'Holy See (vatican city state)'
    },
    {
        countryId: 99,
        code: 'HN',
        name: 'Honduras',
        value: 'Honduras'
    },
    {
        countryId: 100,
        code: 'HK',
        name: 'Hong Kong',
        value: 'Hong Kong'
    },
    {
        countryId: 101,
        code: 'HU',
        name: 'Hungary',
        value: 'Hungary'
    },
    {
        countryId: 102,
        code: 'IS',
        name: 'Iceland',
        value: 'Iceland'
    },
    {
        countryId: 103,
        code: 'IN',
        name: 'India',
        value: 'India'
    },
    {
        countryId: 104,
        code: 'ID',
        name: 'Indonesia',
        value: 'Indonesia'
    },
    {
        countryId: 105,
        code: 'IR',
        name: 'Iran, Islamic Republic of',
        value: 'Iran, Islamic Republic of'
    },
    {
        countryId: 106,
        code: 'IQ',
        name: 'Iraq',
        value: 'Iraq'
    },
    {
        countryId: 107,
        code: 'IE',
        name: 'Ireland',
        value: 'Ireland'
    },
    {
        countryId: 108,
        code: 'IM',
        name: 'Isle of Man',
        value: 'Isle of Man'
    },
    {
        countryId: 109,
        code: 'IL',
        name: 'Israel',
        value: 'Israel'
    },
    {
        countryId: 110,
        code: 'IT',
        name: 'Italy',
        value: 'Italy'
    },
    {
        countryId: 111,
        code: 'JM',
        name: 'Jamaica',
        value: 'Jamaica'
    },
    {
        countryId: 112,
        code: 'JP',
        name: 'Japan',
        value: 'Japan'
    },
    {
        countryId: 113,
        code: 'JE',
        name: 'Jersey',
        value: 'Jersey'
    },
    {
        countryId: 114,
        code: 'JO',
        name: 'Jordan',
        value: 'Jordan'
    },
    {
        countryId: 115,
        code: 'KZ',
        name: 'Kazakhstan',
        value: 'Kazakhstan'
    },
    {
        countryId: 116,
        code: 'KE',
        name: 'Kenya',
        value: 'Kenya'
    },
    {
        countryId: 117,
        code: 'KI',
        name: 'Kiribati',
        value: 'Kiribati'
    },
    {
        countryId: 118,
        code: 'KP',
        name: 'Korea, Democratic People\'s Republic of',
        value: 'Korea, Democratic People\'s Republic of'
    },
    {
        countryId: 119,
        code: 'KR',
        name: 'Korea, Republic of',
        value: 'Korea, Republic of'
    },
    {
        countryId: 120,
        code: 'KW',
        name: 'Kuwait',
        value: 'Kuwait'
    },
    {
        countryId: 121,
        code: 'KG',
        name: 'Kyrgyzstan',
        value: 'Kyrgyzstan'
    },
    {
        countryId: 122,
        code: 'LA',
        name: 'Lao People\'s Democratic Republic',
        value: 'Lao People\'s Democratic Republic'
    },
    {
        countryId: 123,
        code: 'LV',
        name: 'Latvia',
        value: 'Latvia'
    },
    {
        countryId: 124,
        code: 'LB',
        name: 'Lebanon',
        value: 'Lebanon'
    },
    {
        countryId: 125,
        code: 'LS',
        name: 'Lesotho',
        value: 'Lesotho'
    },
    {
        countryId: 126,
        code: 'LR',
        name: 'Liberia',
        value: 'Liberia'
    },
    {
        countryId: 127,
        code: 'LY',
        name: 'Libya',
        value: 'Libya'
    },
    {
        countryId: 128,
        code: 'LI',
        name: 'Liechtenstein',
        value: 'Liechtenstein'
    },
    {
        countryId: 129,
        code: 'LT',
        name: 'Lithuania',
        value: 'Lithuania'
    },
    {
        countryId: 130,
        code: 'LU',
        name: 'Luxembourg',
        value: 'Luxembourg'
    },
    {
        countryId: 131,
        code: 'MO',
        name: 'Macao',
        value: 'Macao'
    },
    {
        countryId: 132,
        code: 'MK',
        name: 'Macedonia, the Former Yugoslav Republic of',
        value: 'Macedonia, the Former Yugoslav Republic of'
    },
    {
        countryId: 133,
        code: 'MG',
        name: 'Madagascar',
        value: 'Madagascar'
    },
    {
        countryId: 134,
        code: 'MW',
        name: 'Malawi',
        value: 'Malawi'
    },
    {
        countryId: 135,
        code: 'MY',
        name: 'Malaysia',
        value: 'Malaysia'
    },
    {
        countryId: 136,
        code: 'MV',
        name: 'Maldives',
        value: 'Maldives'
    },
    {
        countryId: 137,
        code: 'ML',
        name: 'Mali',
        value: 'Mali'
    },
    {
        countryId: 138,
        code: 'MT',
        name: 'Malta',
        value: 'Malta'
    },
    {
        countryId: 139,
        code: 'MH',
        name: 'Marshall Islands',
        value: 'Marshall Islands'
    },
    {
        countryId: 140,
        code: 'MQ',
        name: 'Martinique',
        value: 'Martinique'
    },
    {
        countryId: 141,
        code: 'MR',
        name: 'Mauritania',
        value: 'Mauritania'
    },
    {
        countryId: 142,
        code: 'MU',
        name: 'Mauritius',
        value: 'Mauritius'
    },
    {
        countryId: 143,
        code: 'YT',
        name: 'Mayotte',
        value: 'Mayotte'
    },
    {
        countryId: 144,
        code: 'MX',
        name: 'Mexico',
        value: 'Mexico'
    },
    {
        countryId: 145,
        code: 'FM',
        name: 'Micronesia, Federated States of',
        value: 'Micronesia, Federated States of'
    },
    {
        countryId: 146,
        code: 'MD',
        name: 'Moldova, Republic of',
        value: 'Moldova, Republic of'
    },
    {
        countryId: 147,
        code: 'MC',
        name: 'Monaco',
        value: 'Monaco'
    },
    {
        countryId: 148,
        code: 'MN',
        name: 'Mongolia',
        value: 'Mongolia'
    },
    {
        countryId: 149,
        code: 'ME',
        name: 'Montenegro',
        value: 'Montenegro'
    },
    {
        countryId: 150,
        code: 'MS',
        name: 'Montserrat',
        value: 'Montserrat'
    },
    {
        countryId: 151,
        code: 'MA',
        name: 'Morocco',
        value: 'Morocco'
    },
    {
        countryId: 152,
        code: 'MZ',
        name: 'Mozambique',
        value: 'Mozambique'
    },
    {
        countryId: 153,
        code: 'MM',
        name: 'Myanmar',
        value: 'Myanmar'
    },
    {
        countryId: 154,
        code: 'NA',
        name: 'Namibia',
        value: 'Namibia'
    },
    {
        countryId: 155,
        code: 'NR',
        name: 'Nauru',
        value: 'Nauru'
    },
    {
        countryId: 156,
        code: 'NP',
        name: 'Nepal',
        value: 'Nepal'
    },
    {
        countryId: 157,
        code: 'NL',
        name: 'Netherlands',
        value: 'Netherlands'
    },
    {
        countryId: 158,
        code: 'NC',
        name: 'New Caledonia',
        value: 'New Caledonia'
    },
    {
        countryId: 159,
        code: 'NZ',
        name: 'New Zealand',
        value: 'New Zealand'
    },
    {
        countryId: 160,
        code: 'NI',
        name: 'Nicaragua',
        value: 'Nicaragua'
    },
    {
        countryId: 161,
        code: 'NE',
        name: 'Niger',
        value: 'Niger'
    },
    {
        countryId: 162,
        code: 'NG',
        name: 'Nigeria',
        value: 'Nigeria'
    },
    {
        countryId: 163,
        code: 'NU',
        name: 'Niue',
        value: 'Niue'
    },
    {
        countryId: 164,
        code: 'NF',
        name: 'Norfolk Island',
        value: 'Norfolk Island'
    },
    {
        countryId: 165,
        code: 'MP',
        name: 'Northern Mariana Islands',
        value: 'Northern Mariana Islands'
    },
    {
        countryId: 166,
        code: 'NO',
        name: 'Norway',
        value: 'Norway'
    },
    {
        countryId: 167,
        code: 'OM',
        name: 'Oman',
        value: 'Oman'
    },
    {
        countryId: 168,
        code: 'PK',
        name: 'Pakistan',
        value: 'Pakistan'
    },
    {
        countryId: 169,
        code: 'PW',
        name: 'Palau',
        value: 'Palau'
    },
    {
        countryId: 170,
        code: 'PS',
        name: 'Palestinian Territory, Occupied',
        value: 'Palestinian Territory, Occupied'
    },
    {
        countryId: 171,
        code: 'PA',
        name: 'Panama',
        value: 'Panama'
    },
    {
        countryId: 172,
        code: 'PG',
        name: 'Papua New Guinea',
        value: 'Papua New Guinea'
    },
    {
        countryId: 173,
        code: 'PY',
        name: 'Paraguay',
        value: 'Paraguay'
    },
    {
        countryId: 174,
        code: 'PE',
        name: 'Peru',
        value: 'Peru'
    },
    {
        countryId: 175,
        code: 'PH',
        name: 'Philippines',
        value: 'Philippines'
    },
    {
        countryId: 176,
        code: 'PN',
        name: 'Pitcairn',
        value: 'Pitcairn'
    },
    {
        countryId: 177,
        code: 'PL',
        name: 'Poland',
        value: 'Poland'
    },
    {
        countryId: 178,
        code: 'PT',
        name: 'Portugal',
        value: 'Portugal'
    },
    {
        countryId: 179,
        code: 'PR',
        name: 'Puerto Rico',
        value: 'Puerto Rico'
    },
    {
        countryId: 180,
        code: 'QA',
        name: 'Qatar',
        value: 'Qatar'
    },
    {
        countryId: 181,
        code: 'RE',
        name: 'Réunion',
        value: 'Réunion'
    },
    {
        countryId: 182,
        code: 'RO',
        name: 'Romania',
        value: 'Romania'
    },
    {
        countryId: 183,
        code: 'RU',
        name: 'Russian Federation',
        value: 'Russian Federation'
    },
    {
        countryId: 184,
        code: 'RW',
        name: 'Rwanda',
        value: 'Rwanda'
    },
    {
        countryId: 185,
        code: 'BL',
        name: 'Saint Barthélemy',
        value: 'Saint Barthélemy'
    },
    {
        countryId: 186,
        code: 'SH',
        name: 'Saint Helena, Ascension and Tristan da Cunha',
        value: 'Saint Helena, Ascension and Tristan da Cunha'
    },
    {
        countryId: 187,
        code: 'KN',
        name: 'Saint Kitts and Nevis',
        value: 'Saint Kitts and Nevis'
    },
    {
        countryId: 188,
        code: 'LC',
        name: 'Saint Lucia',
        value: 'Saint Lucia'
    },
    {
        countryId: 189,
        code: 'MF',
        name: 'Saint Martin (French Part)',
        value: 'Saint Martin (French Part)'
    },
    {
        countryId: 190,
        code: 'PM',
        name: 'Saint Pierre and Miquelon',
        value: 'Saint Pierre and Miquelon'
    },
    {
        countryId: 191,
        code: 'VC',
        name: 'Saint Vincent and the Grenadines',
        value: 'Saint Vincent and the Grenadines'
    },
    {
        countryId: 192,
        code: 'WS',
        name: 'Samoa',
        value: 'Samoa'
    },
    {
        countryId: 193,
        code: 'SM',
        name: 'San Marino',
        value: 'San Marino'
    },
    {
        countryId: 194,
        code: 'ST',
        name: 'Sao Tome and Principe',
        value: 'Sao Tome and Principe'
    },
    {
        countryId: 195,
        code: 'SA',
        name: 'Saudi Arabia',
        value: 'Saudi Arabia'
    },
    {
        countryId: 196,
        code: 'SN',
        name: 'Senegal',
        value: 'Senegal'
    },
    {
        countryId: 197,
        code: 'RS',
        name: 'Serbia',
        value: 'Serbia'
    },
    {
        countryId: 198,
        code: 'SC',
        name: 'Seychelles',
        value: 'Seychelles'
    },
    {
        countryId: 199,
        code: 'SL',
        name: 'Sierra Leone',
        value: 'Sierra Leone'
    },
    {
        countryId: 200,
        code: 'SG',
        name: 'Singapore',
        value: 'Singapore'
    },
    {
        countryId: 201,
        code: 'SX',
        name: 'Sint Maarten (Dutch Part)',
        value: 'Sint Maarten (Dutch Part)'
    },
    {
        countryId: 202,
        code: 'SK',
        name: 'Slovakia',
        value: 'Slovakia'
    },
    {
        countryId: 203,
        code: 'SI',
        name: 'Slovenia',
        value: 'Slovenia'
    },
    {
        countryId: 204,
        code: 'SB',
        name: 'Solomon Islands',
        value: 'Solomon Islands'
    },
    {
        countryId: 205,
        code: 'SO',
        name: 'Somalia',
        value: 'Somalia'
    },
    {
        countryId: 206,
        code: 'ZA',
        name: 'South Africa',
        value: 'South Africa'
    },
    {
        countryId: 207,
        code: 'GS',
        name: 'South Georgia and The South Sandwich Islands',
        value: 'South Georgia and The South Sandwich Islands'
    },
    {
        countryId: 208,
        code: 'SS',
        name: 'South Sudan',
        value: 'South Sudan'
    },
    {
        countryId: 209,
        code: 'ES',
        name: 'Spain',
        value: 'Spain'
    },
    {
        countryId: 210,
        code: 'LK',
        name: 'Sri Lanka',
        value: 'Sri Lanka'
    },
    {
        countryId: 211,
        code: 'SD',
        name: 'Sudan',
        value: 'Sudan'
    },
    {
        countryId: 212,
        code: 'SR',
        name: 'Suriname',
        value: 'Suriname'
    },
    {
        countryId: 213,
        code: 'SJ',
        name: 'Svalbard and Jan Mayen',
        value: 'Svalbard and Jan Mayen'
    },
    {
        countryId: 214,
        code: 'SZ',
        name: 'Swaziland',
        value: 'Swaziland'
    },
    {
        countryId: 215,
        code: 'SE',
        name: 'Sweden',
        value: 'Sweden'
    },
    {
        countryId: 216,
        code: 'CH',
        name: 'Switzerland',
        value: 'Switzerland'
    },
    {
        countryId: 217,
        code: 'SY',
        name: 'Syrian Arab Republic',
        value: 'Syrian Arab Republic'
    },
    {
        countryId: 218,
        code: 'TW',
        name: 'Taiwan, Province of China',
        value: 'Taiwan, Province of China'
    },
    {
        countryId: 219,
        code: 'TJ',
        name: 'Tajikistan',
        value: 'Tajikistan'
    },
    {
        countryId: 220,
        code: 'TZ',
        name: 'Tanzania, United Republic of',
        value: 'Tanzania, United Republic of'
    },
    {
        countryId: 221,
        code: 'TH',
        name: 'Thailand',
        value: 'Thailand'
    },
    {
        countryId: 222,
        code: 'TL',
        name: 'Timor-Leste',
        value: 'Timor-Leste'
    },
    {
        countryId: 223,
        code: 'TG',
        name: 'Togo',
        value: 'Togo'
    },
    {
        countryId: 224,
        code: 'TK',
        name: 'Tokelau',
        value: 'Tokelau'
    },
    {
        countryId: 225,
        code: 'TO',
        name: 'Tonga',
        value: 'Tonga'
    },
    {
        countryId: 226,
        code: 'TT',
        name: 'Trinidad and Tobago',
        value: 'Trinidad and Tobago'
    },
    {
        countryId: 227,
        code: 'TN',
        name: 'Tunisia',
        value: 'Tunisia'
    },
    {
        countryId: 228,
        code: 'TR',
        name: 'Turkey',
        value: 'Turkey'
    },
    {
        countryId: 229,
        code: 'TM',
        name: 'Turkmenistan',
        value: 'Turkmenistan'
    },
    {
        countryId: 230,
        code: 'TC',
        name: 'Turks and Caicos Islands',
        value: 'Turks and Caicos Islands'
    },
    {
        countryId: 231,
        code: 'TV',
        name: 'Tuvalu',
        value: 'Tuvalu'
    },
    {
        countryId: 232,
        code: 'UG',
        name: 'Uganda',
        value: 'Uganda'
    },
    {
        countryId: 233,
        code: 'UA',
        name: 'Ukraine',
        value: 'Ukraine'
    },
    {
        countryId: 234,
        code: 'AE',
        name: 'United Arab Emirates',
        value: 'United Arab Emirates'
    },
    {
        countryId: 235,
        code: 'GB',
        name: 'United Kingdom',
        value: 'United Kingdom'
    },
    {
        countryId: 236,
        code: 'US',
        name: 'United States',
        value: 'United States'
    },
    {
        countryId: 237,
        code: 'UM',
        name: 'United States Minor Outlying Islands',
        value: 'United States Minor Outlying Islands'
    },
    {
        countryId: 238,
        code: 'UY',
        name: 'Uruguay',
        value: 'Uruguay'
    },
    {
        countryId: 239,
        code: 'UZ',
        name: 'Uzbekistan',
        value: 'Uzbekistan'
    },
    {
        countryId: 240,
        code: 'VU',
        name: 'Vanuatu',
        value: 'Vanuatu'
    },
    {
        countryId: 241,
        code: 'VE',
        name: 'Venezuela, Bolivarian Republic of',
        value: 'Venezuela, Bolivarian Republic of'
    },
    {
        countryId: 242,
        code: 'VN',
        name: 'Vietnam',
        value: 'Vietnam'
    },
    {
        countryId: 243,
        code: 'VG',
        name: 'Virgin Islands, British',
        value: 'Virgin Islands, British'
    },
    {
        countryId: 244,
        code: 'VI',
        name: 'Virgin Islands, U.S.',
        value: 'Virgin Islands, U.S.'
    },
    {
        countryId: 245,
        code: 'WF',
        name: 'Wallis and Futuna',
        value: 'Wallis and Futuna'
    },
    {
        countryId: 246,
        code: 'EH',
        name: 'Western Sahara',
        value: 'Western Sahara'
    },
    {
        countryId: 247,
        code: 'YE',
        name: 'Yemen',
        value: 'Yemen'
    },
    {
        countryId: 248,
        code: 'ZM',
        name: 'Zambia',
        value: 'Zambia'
    },
    {
        countryId: 249,
        code: 'ZW',
        name: 'Zimbabwe',
        value: 'Zimbabwe'
    }
];

class CountriesUtils {
    constructor() {
        this.countriesCache = [];
    }
    static get instance() {
        if (!this._instance) {
            this._instance = new CountriesUtils();
        }
        return this._instance;
    }
    clearCache() {
        this.countriesCache = [];
    }
    getCountriesList() {
        if (this.countriesCache.length === 0) {
            this.countriesCache = Countries;
        }
        return this.countriesCache;
    }
}

class HttpErrorResponse extends Error {
    constructor(errorResponse, body) {
        super(`HTTP Error ${errorResponse.status} ${errorResponse.statusText}`);
        this.name = 'HttpError';
        this.response = errorResponse;
        this.body = body;
    }
}

const HttpRetryStrategy = ({ maxRetryAttempts = 3, scalingDuration = 1000, includedStatusCodes = [503], retryHeaderNeeded = '' } = {}) => (attempts) => attempts.pipe(mergeMap((error, i) => {
    const retryAttempt = i + 1;
    const retryHeaderValue = retryHeaderNeeded && (error && error.response && error.response.headers) ? error.response.headers.get(retryHeaderNeeded) : null;
    const hasRetryStatusCode = error.response ? includedStatusCodes.find((e) => e === error.response.status) : false;
    const shouldRetry = hasRetryStatusCode && (retryHeaderValue || retryHeaderNeeded === '');
    if (retryHeaderValue) {
        if (!isNaN(retryHeaderValue)) {
            scalingDuration = parseInt(retryHeaderValue, 10);
        }
    }
    if (retryAttempt > maxRetryAttempts || !shouldRetry) {
        return throwError(error);
    }
    return timer(retryAttempt * scalingDuration);
}));

class HttpUtils {
    static generatePrefix(options) {
        var _a;
        const hasOverridePrefixValue = (_a = options === null || options === void 0 ? void 0 : options.overridePrefixValue) === null || _a === void 0 ? void 0 : _a.length;
        if (hasOverridePrefixValue !== undefined) {
            return options === null || options === void 0 ? void 0 : options.overridePrefixValue;
        }
        if (!ConfigurationService.instance.get(ConfigurationService.COMMON_KEYS.ApiFacadeOptOut)) {
            return ConfigurationService.instance.get(ConfigurationService.COMMON_KEYS.ApiFacadeEndpoint);
        }
        else {
            return ConfigurationService.instance.get(ConfigurationService.COMMON_KEYS.ApiServerBaseUrl);
        }
    }
    static getTraceabilityHeaderValue() {
        const traceabilityHeaderValue = ConfigurationService.instance.get('traceability_key');
        if (traceabilityHeaderValue) {
            return traceabilityHeaderValue.match(/[^\/]+/)[0];
        }
        const appContext = ConfigurationService.instance.get('appContext');
        if (appContext) {
            return appContext.match(/[^\/]+/)[0];
        }
        return URLUtils.getAppContextFromUrl();
    }
    static buildOptions(options, requireToken) {
        const traceabilityHeaderValue = HttpUtils.getTraceabilityHeaderValue();
        const fetchOptions = {};
        let fetchHeaders = {};
        if (options && options.isAcdCall) {
            fetchHeaders = {
                'Content-Type': 'application/json',
                'Originating-Service-Identifier': traceabilityHeaderValue
            };
        }
        else if (options && options.isSvgCall) {
            fetchHeaders = {
                'If-Modified-Since': 'Mon, 26 Jul 1997 05:00:00 GMT',
                'Cache-Control': 'no-cache',
                Pragma: 'no-cache',
                'Content-Type': 'image/svg+xml',
                'Originating-Service-Identifier': traceabilityHeaderValue
            };
        }
        else {
            fetchHeaders = {
                'If-Modified-Since': 'Mon, 26 Jul 1997 05:00:00 GMT',
                'Cache-Control': 'no-cache',
                Pragma: 'no-cache',
                'Content-Type': 'application/json',
                'Originating-Service-Identifier': traceabilityHeaderValue
            };
        }
        let token = '';
        if (options && options.method) {
            fetchOptions.method = options.method;
        }
        if (options && options.body) {
            fetchOptions.body = typeof options.body === 'string' ? options.body : JSON.stringify(options.body);
        }
        if (options && options.withCredentials) {
            fetchOptions.credentials = 'include';
        }
        if (options && options.headers) {
            options.headers.forEach((header) => {
                fetchHeaders[header.name] = header.value;
            });
        }
        if (requireToken) {
            token = User.instance.token;
            if (token) {
                fetchHeaders.Authorization = `Bearer ${token}`;
            }
            else {
                console.error('No token set on User model');
            }
        }
        if (Object.keys(fetchHeaders).length > 0) {
            fetchOptions.headers = fetchHeaders;
        }
        return fetchOptions;
    }
    static buildUrl(url, params, prefix) {
        const queryParameters = {};
        /* Reduce double slash // to single slash / at the start of the url */
        url = url.replace(/^\/\/+/, '/');
        if (prefix) {
            if (!prefix.endsWith('/')) {
                prefix = prefix + '/';
            }
            if (url.startsWith('/')) {
                url = url.substring(1);
            }
            url = `${prefix}${url}`;
        }
        if (!params) {
            console.log('URL1', url);
            return url;
        }
        Object.keys(params).forEach((paramName) => {
            if (url.includes(`:${paramName}`)) {
                url = url.replace(`:${paramName}`, params[paramName]);
            }
            else {
                queryParameters[paramName] = params[paramName];
            }
        });
        const queryString = Object.keys(queryParameters)
            .map((key) => key + '=' + queryParameters[key])
            .join('&');
        if (queryString) {
            url += url.includes('?') ? `&${queryString}` : `?${queryString}`;
        }
        console.log('URL2', url);
        return url;
    }
    static defaultRetryOptions() {
        return {
            maxRetryAttempts: 3,
            scalingDuration: 1000,
            includedStatusCodes: [503],
            retryHeaderNeeded: CXOneConstants.HTTP_HEADERS['retry-after']
        };
    }
    static convertResponse(body) {
        if (body === null || body === undefined) {
            return body;
        }
        if (typeof body !== 'object') {
            return body;
        }
        for (const key of Object.keys(body)) {
            const value = body[key];
            if (typeof value === 'string') {
                if (DateUtils.isISO8601Date(value)) {
                    body[key] = new Date(value);
                }
            }
            else if (typeof value === 'object') {
                this.convertResponse(value);
            }
        }
        return body;
    }
    static standardResponse(observableIn, retryOptions) {
        retryOptions = retryOptions || this.defaultRetryOptions();
        return observableIn
            .pipe(switchMap((response) => {
            const jsonResponse = response.text();
            return new Promise((resolve, reject) => {
                jsonResponse.then((data) => {
                    try {
                        data = JSON.parse(data);
                    }
                    catch (_a) { }
                    if (response.status === 401 && CoreUtils.isTokenExpired()) {
                        SubscribeService.instance.invokeSubscribeCallbacks(CXOneConstants.EVENTS.TOKEN_EXPIRED, null, false);
                    }
                    if (response.ok) {
                        resolve(HttpUtils.convertResponse(data));
                    }
                    else {
                        reject(new HttpErrorResponse(response, data));
                    }
                });
            });
        }))
            .pipe(retryWhen(HttpRetryStrategy(retryOptions)));
    }
    static makeXHRRequest(method, url, options) {
        return new Promise(function (resolve, reject) {
            const xhr = new XMLHttpRequest();
            xhr.open(method, url);
            if (options && options.headers) {
                const headers = Object.entries(options.headers);
                headers.forEach(((value) => {
                    xhr.setRequestHeader(value[0], value[1]);
                }));
            }
            xhr.onload = function () {
                if (this.status >= 200 && this.status < 300) {
                    resolve(xhr.response);
                }
                else {
                    reject({
                        status: this.status,
                        statusText: xhr.statusText
                    });
                }
            };
            xhr.onerror = function () {
                reject({
                    status: this.status,
                    statusText: xhr.statusText
                });
            };
            if (options && options.body && method !== 'GET') {
                xhr.send(JSON.stringify(options.body));
            }
            else {
                xhr.send();
            }
        });
    }
    static internalGet(url, options, requireToken, retryOptions, prefixURL) {
        const defaultOptions = {
            method: 'GET'
        };
        url = HttpUtils.buildUrl(url, options && options.params ? options.params : null, prefixURL);
        console.debug('HttpUtils.url', url);
        const optionsForRequest = HttpUtils.buildOptions(Object.assign(Object.assign({}, defaultOptions), options), requireToken);
        if (options && options.useXhrRequest) {
            return HttpUtils.makeXHRRequest('GET', url, optionsForRequest);
        }
        return lastValueFrom(HttpUtils.standardResponse(fromFetch(url, optionsForRequest), retryOptions));
    }
    /**
     * @deprecated Use {@link apiGet}  or  {@link staticGet} instead
     */
    static get(url, options, requireToken, retryOptions) {
        return HttpUtils.internalGet(url, options, requireToken, retryOptions);
    }
    static apiGet(url, options, requireToken, retryOptions) {
        const prefix = HttpUtils.generatePrefix(options);
        return HttpUtils.internalGet(url, options, requireToken, retryOptions, prefix);
    }
    static staticGet(url, options, requireToken, retryOptions) {
        const prefix = ConfigurationService.instance.get(ConfigurationService.COMMON_KEYS.StaticBaseUrl);
        return HttpUtils.internalGet(url, options, requireToken, retryOptions, prefix);
    }
    static internalPost(url, body, options, requireToken, retryOptions, prefixURL) {
        const defaultOptions = {
            method: 'POST'
        };
        url = HttpUtils.buildUrl(url, options && options.params ? options.params : null, prefixURL);
        console.debug('HttpUtils.url', url);
        return lastValueFrom(HttpUtils.standardResponse(fromFetch(url, HttpUtils.buildOptions(Object.assign(Object.assign(Object.assign({}, defaultOptions), { body }), options), requireToken)), retryOptions));
    }
    /**
     * @deprecated Use {@link apiPost} instead
     */
    static post(url, body, options, requireToken, retryOptions) {
        return HttpUtils.internalPost(url, body, options, requireToken, retryOptions);
    }
    static apiPost(url, body, options, requireToken, retryOptions) {
        const prefix = HttpUtils.generatePrefix(options);
        return HttpUtils.internalPost(url, body, options, requireToken, retryOptions, prefix);
    }
    static internalPut(url, body, options, requireToken, retryOptions, prefixURL) {
        const defaultOptions = {
            method: 'PUT'
        };
        url = HttpUtils.buildUrl(url, options && options.params ? options.params : null, prefixURL);
        console.debug('HttpUtils.url', url);
        return lastValueFrom(HttpUtils.standardResponse(fromFetch(url, HttpUtils.buildOptions(Object.assign(Object.assign(Object.assign({}, defaultOptions), { body }), options), requireToken)), retryOptions));
    }
    /**
     * @deprecated Use {@link apiPut} instead
     */
    static put(url, body, options, requireToken, retryOptions) {
        return HttpUtils.internalPut(url, body, options, requireToken, retryOptions);
    }
    static apiPut(url, body, options, requireToken, retryOptions) {
        const prefix = HttpUtils.generatePrefix(options);
        return HttpUtils.internalPut(url, body, options, requireToken, retryOptions, prefix);
    }
    static internalPatch(url, body, options, requireToken, retryOptions, prefixURL) {
        const defaultOptions = {
            method: 'PATCH'
        };
        url = HttpUtils.buildUrl(url, options && options.params ? options.params : null, prefixURL);
        console.debug('HttpUtils.url', url);
        return lastValueFrom(HttpUtils.standardResponse(fromFetch(url, HttpUtils.buildOptions(Object.assign(Object.assign(Object.assign({}, defaultOptions), { body }), options), requireToken)), retryOptions));
    }
    /**
     * @deprecated Use {@link apiPatch} instead
     */
    static patch(url, body, options, requireToken, retryOptions) {
        return HttpUtils.internalPatch(url, body, options, requireToken, retryOptions);
    }
    static apiPatch(url, body, options, requireToken, retryOptions) {
        const prefix = HttpUtils.generatePrefix(options);
        return HttpUtils.internalPatch(url, body, options, requireToken, retryOptions, prefix);
    }
    static internalDelete(url, body, options, requireToken, retryOptions, prefixURL) {
        const defaultOptions = {
            method: 'DELETE'
        };
        url = HttpUtils.buildUrl(url, options && options.params ? options.params : null, prefixURL);
        console.debug('HttpUtils.url', url);
        return lastValueFrom(HttpUtils.standardResponse(fromFetch(url, HttpUtils.buildOptions(Object.assign(Object.assign(Object.assign({}, defaultOptions), { body }), options), requireToken)), retryOptions));
    }
    /**
     * @deprecated Use {@link apiDelete} instead
     */
    static delete(url, body, options, requireToken, retryOptions) {
        return HttpUtils.internalDelete(url, body, options, requireToken, retryOptions);
    }
    static apiDelete(url, body, options, requireToken, retryOptions) {
        const prefix = HttpUtils.generatePrefix(options);
        return HttpUtils.internalDelete(url, body, options, requireToken, retryOptions, prefix);
    }
}

class CommonLocalizationUtils {
    static getCurrentLocaleFromBrowser() {
        return window.navigator.language;
    }
    static getMonthsByBrowserLocale() {
        const months = [];
        for (let i = 0; i < 12; i += 1) {
            months.push({ id: i, name: new Date(2019, i, 1).toLocaleString(this.getCurrentLocaleFromBrowser(), { month: 'long' }) });
        }
        return months;
    }
}

class MessageFormat {
    constructor(messageFromServer) {
        const objMessageFromSever = JSON.parse(messageFromServer);
        this.messageCommnad = objMessageFromSever.command;
        this.messageData = objMessageFromSever.data;
        this.messageHeader = objMessageFromSever.headers;
    }
}
class InternalNotificationService {
    constructor(sockJS) {
        this.heartbeatWSMaxTimeInterval = 60 * 1000;
        this.callbackHandler = null;
        this.timeIntervalForWSConnection = 0;
        this.lastHeartbeatWSConnectionCheckTime = 0;
        this.isClientInitiateCloseConnection = false;
        this.counterTimeOfConnectionTrying = 0;
        this.replaySubjects$ = {};
        this.SockJS = sockJS;
        this.setApiBaseUrl('');
        this.reset();
        this.isWebWorker = typeof DedicatedWorkerGlobalScope !== 'undefined' && self instanceof DedicatedWorkerGlobalScope;
        console.debug('NotificationService is running in WebWorker: ', this.isWebWorker);
    }
    isWebWorkerTokenExpired() {
        if (this.webWorkerTokenExpirationTime) {
            const expirationTime = new Date(this.webWorkerTokenExpirationTime).getTime();
            return expirationTime < DateUtils.now().getTime();
        }
        return undefined;
    }
    updateWebWorkerTokenExpirationTime(tokenExpirationTime) {
        console.debug('Internal notification service - Token Time is updated: ', tokenExpirationTime);
        this.webWorkerTokenExpirationTime = tokenExpirationTime;
    }
    isNotificationWSConnected() {
        console.log(`isNotificationWSConnected is web worker: ${this.isWebWorker}`);
        return this.socket && this.socket.readyState === 1;
    }
    sendToSubscribes(key, value) {
        if (!this.replaySubjects$[key]) {
            this.replaySubjects$[key] = new ReplaySubject(InternalNotificationService.replayLimit);
        }
        this.replaySubjects$[key].next(value);
    }
    subscribeToSubject(key) {
        if (!this.replaySubjects$[key]) {
            this.replaySubjects$[key] = new ReplaySubject(InternalNotificationService.replayLimit);
        }
        return this.replaySubjects$[key];
    }
    getWindowObject() {
        // If running in a web worker, return a mock window object
        if (this.isWebWorker) {
            return {
                location: {
                    href: self.location.href
                },
                setTimeout: self.setTimeout.bind(self),
                clearTimeout: self.clearTimeout.bind(self),
                setInterval: self.setInterval.bind(self),
                clearInterval: self.clearInterval.bind(self)
            };
        }
        // If running in a browser, return the actual window object
        return window;
    }
    static getInstance(sockJS) {
        if (!InternalNotificationService._instance) {
            InternalNotificationService._instance = new InternalNotificationService(sockJS);
        }
        return InternalNotificationService._instance;
    }
    reset() {
        this.objSockJS = null;
        this.socket = null;
        this.timeIntervalForWSConnection = 0;
        this.isClientInitiateCloseConnection = false;
        this.counterTimeOfConnectionTrying = 0;
        this.notificationCustomIdentifier = null;
        this.idRetryConnectInterval = null;
        this.heartbeatWSInterval = null;
        this.angularZone = null;
    }
    setApiBaseUrl(url) {
        this.apiServerBaseUrl = url;
    }
    runOutsideAngularZone(func) {
        // If running in a web worker, just run the function
        if (this.isWebWorker) {
            return func();
        }
        const Zone = window.Zone;
        if (Zone && Zone.current.name === 'angular') {
            this.angularZone = Zone.current;
            console.debug('Running function ' + func.name + ' outside of Angular Zone');
            return this.angularZone.parent.run(func);
        }
        return func();
    }
    runInsideAngularZone(func) {
        // If running in a web worker, just run the function
        if (this.isWebWorker) {
            return func();
        }
        if (this.angularZone) {
            console.debug('Running function ' + func.name + ' inside of Angular Zone');
            return this.angularZone.run(func);
        }
        return func();
    }
    getCustomIdentifier(options) {
        const customIdentifier = this.isWebWorker ? options.customIdentifier : (options && options.customIdentifier || User.instance.id);
        return encodeURIComponent(customIdentifier);
    }
    createNewSockJS(apiServerBaseUrl, options) {
        // 'websocket' -- out from the list until ELB will support this protocol
        // 'iframe-htmlfile','iframe-eventsource' - Invalid 'X-Frame-Options' header encountered when loading
        const protocols = ['websocket', 'xhr-streaming', 'xhr-polling', 'xdr-streaming',
            'xdr-polling', 'iframe-xhr-polling', 'jsonp-polling'];
        const sockJSOptions = {
            transports: protocols,
            server: this.getCustomIdentifier(options)
        };
        console.debug('SJS: Creating new Sock JS Object with options: ', sockJSOptions);
        return new this.SockJS(`${apiServerBaseUrl}/ws/notifications`, null, sockJSOptions);
    }
    getSockJSObject(apiServerBaseUrl, options) {
        const isTokenExpired = this.isWebWorker ? this.isWebWorkerTokenExpired() : CoreUtils.isTokenExpired();
        console.debug('SJS: is user token expired: ', isTokenExpired);
        // 'websocket' -- out from the list until ELB will support this protocol
        // 'iframe-htmlfile','iframe-eventsource' - Invalid 'X-Frame-Options' header encountered when loading
        // const protocols = ['websocket', 'xhr-streaming', 'xhr-polling', 'xdr-streaming',
        //     'xdr-polling', 'iframe-xhr-polling', 'jsonp-polling'];
        if (isTokenExpired) {
            console.debug('Notification service - Token is Expired!');
            SubscribeService.instance.invokeSubscribeCallbacks(CXOneConstants.EVENTS.TOKEN_EXPIRED, null, true);
            this.sendToSubscribes('notificationservice.token-expired', null);
            return false;
        }
        console.debug('SJS: SockJS Object State: ', JSON.stringify(this.objSockJS));
        SubscribeService.instance.invokeSubscribeCallbacks(CXOneConstants.EVENTS.GET_SOCK_JS_OBJECT, JSON.stringify(this.objSockJS), true);
        this.sendToSubscribes('notificationservice.get-obj', JSON.stringify(this.objSockJS));
        if (this.objSockJS === null || this.objSockJS === undefined) {
            console.debug('SJS: Creating new Sock JS Object');
            SubscribeService.instance.invokeSubscribeCallbacks(CXOneConstants.EVENTS.CREATE_NEW_SOCK_JS_OBJECT, null, true);
            this.sendToSubscribes('notificationservice.re-open', null);
            this.objSockJS = this.runOutsideAngularZone(() => {
                return this.createNewSockJS(apiServerBaseUrl, options);
            });
        }
        return this.objSockJS;
    }
    authenticateConnection(options) {
        console.debug('SJS: Notification service authenticateConnection ', options);
        const connectObj = {
            command: 'CONNECT',
            headers: {
                sourceApplication: this.getWindowObject().location.href,
                sessionToken: '',
                isSingleSession: false,
                clientLocale: this.isWebWorker ? options.locale : CommonLocalizationUtils.getCurrentLocaleFromBrowser(),
                clientTimezone: this.isWebWorker ? options.timezone : tz.guess(),
                customIdentifier: undefined,
                notificationCustomIdentifier: undefined
            }
        };
        if (options !== undefined) {
            if (options.token) {
                connectObj.headers.sessionToken = options.token;
            }
            if (options.customIdentifier) {
                connectObj.headers.customIdentifier = options.customIdentifier;
            }
            if (options.isSingleSession) {
                connectObj.headers.isSingleSession = options.isSingleSession;
            }
            if (options.notificationCustomIdentifier) {
                this.notificationCustomIdentifier = options.notificationCustomIdentifier;
                connectObj.headers.notificationCustomIdentifier = this.notificationCustomIdentifier;
            }
            console.debug('SJS: [IF] Notification service authenticateConnection connectionObj', connectObj);
            this.socket.send(JSON.stringify(connectObj));
        }
        else if (User.instance != null) {
            connectObj.headers.sessionToken = User.instance.token;
            console.debug('SJS: [ELSE] Notification service authenticateConnection connectionObj', connectObj);
            this.socket.send(JSON.stringify(connectObj));
        }
    }
    resetTryToConnectInterval(command) {
        if (command === 'CONNECTED') {
            this.timeIntervalForWSConnection = 0;
            this.counterTimeOfConnectionTrying = 0;
            this.clearConnectionInterval();
        }
    }
    routeAllNotificationMessagesToCallback(callbackFunc) {
        this.callbackHandler = callbackFunc;
        this.createWebSocketHandshake();
    }
    registerToWSEvents(options) {
        console.debug('SJS: Notification service registerToWSEvents ', this.socket);
        if (this.socket) {
            this.socket.onopen = () => {
                try {
                    this.runInsideAngularZone(() => {
                        console.debug('SJS: OPEN Notification Service', options);
                        // connection established
                        this.authenticateConnection(options);
                    });
                }
                catch (e) {
                    console.error('SJS: Got error while tried to open a WS: ' + e);
                    console.error(e);
                    this.socket.onclose('Got error while tried to open a WS ' + e);
                }
            };
            this.socket.onheartbeat = () => {
                this.lastHeartbeatWSConnectionCheckTime = new Date().getTime();
            };
            this.socket.onmessage = (e) => {
                this.runInsideAngularZone(() => {
                    const strNotification = e.data;
                    const message = new MessageFormat(strNotification);
                    let notificationUuid = 'None';
                    if (message.messageHeader) {
                        notificationUuid = message.messageHeader.notificationUuid;
                    }
                    console.debug('SJS: onmessage received notification uuid: ' + notificationUuid, message);
                    console.debug('SJS: onmessage received, is webWorker: ' + this.isWebWorker);
                    this.resetTryToConnectInterval(message.messageCommnad);
                    // Callback will receive all messages from WS [MFE]
                    if (this.callbackHandler !== null && typeof this.callbackHandler === 'function') {
                        this.callbackHandler(message);
                    }
                    if (message.messageCommnad === 'CONNECTED') {
                        this.sendToSubscribes(message.messageCommnad, message);
                        this.clearHeartbeatWSInterval();
                        this.lastHeartbeatWSConnectionCheckTime = new Date().getTime();
                        this.heartbeatWSInterval = this.getWindowObject().setInterval(this.heartbeatWSIntervalHandler.bind(this), this.heartbeatWSMaxTimeInterval, options);
                    }
                    // consume notifications from gamification wallet template, but those will not be added
                    // to IN_APP notifications(bell icon)
                    if (message.messageHeader &&
                        message.messageHeader.notificationTargetType &&
                        message.messageHeader.notificationTargetType === 'IN_APP') {
                        // in this case, we need to broadcast "IN_APP" for the notificationMenu.js to process/render
                        // generic notification in the Badge
                        this.sendToSubscribes(message.messageHeader.notificationTargetType, e.data);
                    }
                    if (message.messageData && message.messageData.notificationTemplate) {
                        console.trace('onmessage realtime notification uuid' + notificationUuid +
                            ' sent to event: ' + message.messageData.notificationTemplate);
                        // this broadcast is done for any listeners like BROADCAST_MESSAGE_NOTIFICATION template or
                        // grid refresh in the shift bidding manager
                        // $rootScope.$broadcast(message.messageData.notificationTemplate, e.data);
                        // now it supports for subscribe to event
                        this.sendToSubscribes(message.messageData.notificationTemplate, e.data);
                    }
                });
            };
            this.socket.onclose = (_reason) => {
                this.runInsideAngularZone(() => {
                    console.debug(`SJS: CLOSE Notification Service: ${JSON.stringify(_reason)}`);
                    this.sendToSubscribes('notificationservice.close', _reason);
                    console.debug('SJS: Notification service onclose, is WebWorker: ' + this.isWebWorker);
                    this.clearSocketAndReconnectToWs(options);
                });
            };
            this.socket.onerror = (_reason) => {
                this.runInsideAngularZone(() => {
                    console.debug(`SJS: ERROR Notification Service: ${JSON.stringify(_reason)}`);
                    this.sendToSubscribes('notificationservice.error', _reason);
                    console.debug('SJS: Notification service onerror, is WebWorker: ' + this.isWebWorker);
                    this.clearSocketAndReconnectToWs(options);
                });
            };
        }
    }
    createWebSocketHandshake(options) {
        console.debug('SJS: Notification service createWebSocketHandshake: ', options);
        this.apiServerBaseUrl = ConfigurationService.instance.get('websocketServerBaseUrl');
        this.socket = this.getSockJSObject(this.apiServerBaseUrl, options);
        if (this.socket !== null) {
            console.debug('SJS: Notification service createWebSocketHandshake apiServerBaseUrl ' + this.apiServerBaseUrl);
            this.registerToWSEvents(options);
        }
    }
    clearSocketAndReconnectToWs(options) {
        console.debug('SJS: Notification service clearSocketAndReconnectToWs, is WebWorker: ' + this.isWebWorker);
        if (this.socket) {
            this.socket.close();
            delete this.socket.onclose;
            delete this.socket.onmessage;
            delete this.socket.onopen;
            delete this.socket.onerror;
            delete this.socket.onheartbeat;
        }
        this.objSockJS = null;
        this.retryToConnectWithTimeout(options);
    }
    heartbeatWSIntervalHandler(options) {
        const currentTime = new Date().getTime();
        if ((currentTime - this.lastHeartbeatWSConnectionCheckTime) > this.heartbeatWSMaxTimeInterval) {
            console.debug('SJS: Notification service [HB]- Restart WS connection. is WebWorker: ' + this.isWebWorker);
            this.clearSocketAndReconnectToWs(options); // trigger the connection flow.
        }
    }
    hasNotificationCustomIdentifierHeader(msg) {
        return msg.headers && msg.headers.notificationCustomIdentifier;
    }
    sendAcknowledge(notificationAcknowledge) {
        console.debug('SJS: Notification service sendAcknowledge ', notificationAcknowledge);
        if (!notificationAcknowledge.headers) {
            notificationAcknowledge.headers = {};
        }
        if (this.notificationCustomIdentifier && !this.hasNotificationCustomIdentifierHeader(notificationAcknowledge)) {
            notificationAcknowledge.headers.notificationCustomIdentifier = this.notificationCustomIdentifier;
        }
        notificationAcknowledge.headers.sessionToken = User.instance.token;
        this.socket.send(JSON.stringify(notificationAcknowledge));
    }
    getCustomInterval(options) {
        return options && options.timeIntervalForWSConnection;
    }
    calculateConnectionTimeInterval(options) {
        const BASELINE_INTERVAL = this.getCustomInterval(options) || 1000;
        this.timeIntervalForWSConnection = BASELINE_INTERVAL * Math.pow(2, this.counterTimeOfConnectionTrying);
        console.debug('SJS: Notification service retryToConnect counterTimeOfConnectionTrying ' +
            this.counterTimeOfConnectionTrying + ' with interval ' +
            this.timeIntervalForWSConnection);
    }
    clearConnectionInterval() {
        if (this.idRetryConnectInterval) {
            this.getWindowObject().clearTimeout(this.idRetryConnectInterval);
            this.idRetryConnectInterval = null;
        }
    }
    clearHeartbeatWSInterval() {
        if (this.heartbeatWSInterval) {
            this.getWindowObject().clearInterval(this.heartbeatWSInterval);
            this.heartbeatWSInterval = null;
        }
    }
    retryToConnect(options) {
        console.debug('SJS: Notification service retryToConnect isClientInitiateCloseConnection: ' + this.isClientInitiateCloseConnection);
        if (!this.isClientInitiateCloseConnection) {
            this.counterTimeOfConnectionTrying += 1;
            this.socket = this.getSockJSObject(this.apiServerBaseUrl, options);
            if (this.socket === false) {
                this.clearConnectionInterval();
                return false;
            }
            this.registerToWSEvents(options);
            return true;
        }
        else {
            return false;
        }
    }
    retryToConnectWithTimeout(options) {
        this.clearConnectionInterval();
        this.clearHeartbeatWSInterval();
        this.calculateConnectionTimeInterval(options);
        if (this.isWebWorker) {
            options.token = User.instance.token;
        }
        this.idRetryConnectInterval = this.getWindowObject().setTimeout(this.retryToConnect.bind(this), this.timeIntervalForWSConnection, options);
    }
    disconnectWebSocket() {
        if (this.socket) {
            console.debug('SJS: Notification service disconnect');
            this.isClientInitiateCloseConnection = true;
            this.notificationCustomIdentifier = null;
            this.clearConnectionInterval();
            if (this.objSockJS && this.objSockJS._transportClose) {
                try {
                    this.objSockJS._transportClose(2018, 'Transport disconnect');
                }
                catch (err) {
                    console.error('SJS: error disconnectWebSocket', err);
                }
            }
            this.objSockJS = null;
            if (this.socket.onclose) {
                this.socket.close();
            }
        }
    }
    getTimeIntervalForWSConnection() {
        return this.timeIntervalForWSConnection;
    }
}
InternalNotificationService.replayLimit = 50;

class Organization {
    constructor() { }
    static get instance() {
        if (!this._instance) {
            this._instance = new Organization();
            this._instance._organization = {
                customerType: '',
                id: '',
                name: '',
                organizationStatus: {
                    active: '',
                    activeStatus: ''
                },
                timeZone: '',
                userCap: 0,
                licenses: [],
                billingId: ''
            };
        }
        return this._instance;
    }
    get customerType() {
        return this._organization.customerType;
    }
    set customerType(customerType) {
        this._organization.customerType = customerType;
    }
    get id() {
        return this._organization.id;
    }
    set id(id) {
        this._organization.id = id;
    }
    get name() {
        return this._organization.name;
    }
    set name(name) {
        this._organization.name = name;
    }
    get organizationStatus() {
        return this._organization.organizationStatus;
    }
    get timeZone() {
        return this._organization.timeZone;
    }
    set timeZone(timezone) {
        this._organization.timeZone = timezone;
    }
    get userCap() {
        return this._organization.userCap;
    }
    set userCap(userCap) {
        this._organization.userCap = userCap;
    }
    get organization() {
        return this._organization;
    }
    set organization(organization) {
        this._organization = organization;
    }
    get licenses() {
        return this._organization.licenses;
    }
    set licenses(licenses) {
        this._organization.licenses.push(...licenses);
    }
    get billingId() {
        return this._organization.billingId;
    }
    set billingId(billingId) {
        this._organization.billingId = billingId;
    }
}

export { AngularZoneUtils as A, ColorUtils as C, DateConstants as D, HttpErrorResponse as H, InternalNotificationService as I, LocalStorageUtils as L, ObjectUtils as O, ScreenUtils as S, TimeZones as T, URLUtils as U, Weekdays as W, __awaiter as _, AESCrypto as a, ArrayUtils as b, CookieUtils as c, CoreUtils as d, CXOneConstants as e, DateUtils as f, Deferred as g, DomUtils as h, StringUtils as i, SubscribeService as j, ScriptLoaderService as k, TimeZoneUtils as l, WindowService as m, Countries as n, CountriesUtils as o, HttpRetryStrategy as p, HttpUtils as q, ConfigurationService as r, User as s, Organization as t, CommonLocalizationUtils as u, HttpUrlUtils as v };

